import React, { useState, useEffect, useMemo, useRef } from 'react';
import './ParcelManager.css';
import { Field, Icon, ICON, Panel, toNumber, formatHa, nameOf, formatDate, contractExpiry, expiresWithinTwoYears } from './ui';
import firebaseAuthService from '../services/firebaseAuthService';
import universalDataService from '../services/universalDataService';
import cloudSyncService from '../services/cloudSyncService';
import cadastralService from '../services/cadastralService';
import { isVillageName } from '../services/mapService';
import backupService from '../services/backupService';
import deletedItemsService from '../services/deletedItemsService';
import { db } from '../config/firebase';
import { doc, onSnapshot, updateDoc, setDoc } from 'firebase/firestore';

// Custom hook for real-time parcels data
const useRealtimeParcels = (farmId) => {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!farmId) {
      setLoading(false);
      return;
    }

    console.log(`🔥 Setting up real-time listener for farm: ${farmId}`);
    setLoading(true);
    setError(null);

    // Set up real-time listener for farmData document
    const unsubscribe = onSnapshot(
      doc(db, 'farmData', farmId),
      (doc) => {
        console.log('📡 Real-time update received from Firebase');

        if (doc.exists()) {
          const data = doc.data();
          const parcelsData = data.parcelsData?.data || data.parcelsData || [];

          console.log(`✅ Real-time parcels loaded: ${Array.isArray(parcelsData) ? parcelsData.length : 0} items`);
          setParcels(Array.isArray(parcelsData) ? parcelsData : []);

          // Also update localStorage for offline access
          if (Array.isArray(parcelsData) && parcelsData.length > 0) {
            const storageKey = `parcelsData_${farmId}`;
            const dataWrapper = {
              data: parcelsData,
              lastModified: new Date().toISOString(),
              synced: true,
              farmId: farmId,
              dataType: 'parcelsData'
            };
            localStorage.setItem(storageKey, JSON.stringify(dataWrapper));
            console.log('💾 Updated localStorage with real-time data');
          }
        } else {
          console.log('📄 No Firebase document found, starting with empty data');
          setParcels([]);
        }

        setLoading(false);
      },
      (error) => {
        console.error('❌ Real-time listener error:', error);
        setError(error);
        setLoading(false);

        // Fallback to localStorage if real-time fails
        try {
          const storageKey = `parcelsData_${farmId}`;
          const localData = localStorage.getItem(storageKey);
          if (localData) {
            const parsed = JSON.parse(localData);
            const fallbackData = parsed.data || parsed;
            if (Array.isArray(fallbackData)) {
              setParcels(fallbackData);
              console.log('📱 Fallback to localStorage data');
            }
          }
        } catch (fallbackError) {
          console.error('❌ Fallback to localStorage failed:', fallbackError);
        }
      }
    );

    // Cleanup function
    return () => {
      console.log('🔄 Cleaning up real-time listener');
      unsubscribe();
    };
  }, [farmId]);

  return { parcels, loading, error, setParcels };
};

// Real-time save function
const saveRealtimeParcels = async (farmId, parcelsData) => {
  if (!farmId || !Array.isArray(parcelsData)) {
    throw new Error('Invalid farmId or parcels data');
  }

  console.log(`💾 Saving ${parcelsData.length} parcels to Firebase with real-time sync...`);

  try {
    const docRef = doc(db, 'farmData', farmId);

    // Get current document to preserve other data
    const currentDoc = await import('firebase/firestore').then(({ getDoc }) => getDoc(docRef));
    const currentData = currentDoc.exists() ? currentDoc.data() : {};

    // Update only the parcels data, preserve everything else
    const updatedData = {
      ...currentData,
      parcelsData: {
        data: parcelsData,
        lastModified: new Date().toISOString(),
        dataType: 'parcelsData'
      },
      lastModified: new Date().toISOString()
    };

    await setDoc(docRef, updatedData, { mergeFields: ['parcelsData', 'lastModified'] });
    console.log('✅ Real-time save completed - all devices will update automatically!');

    return true;
  } catch (error) {
    console.error('❌ Real-time save failed:', error);
    throw error;
  }
};

// Real-time hook for company settings
const useRealtimeCompanySettings = (farmId) => {
  const [companySettings, setCompanySettings] = useState({
    companies: [],
    locations: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!farmId) {
      setLoading(false);
      return;
    }

    console.log('🔥 Setting up real-time listener for company settings:', farmId);
    setLoading(true);

    const unsubscribe = onSnapshot(
      doc(db, 'farmData', farmId),
      (doc) => {
        console.log('📡 Real-time company settings update received');

        if (doc.exists()) {
          const data = doc.data();

          // Extract company settings data
          const settingsData = data.companySettings?.data || {};
          const companies = settingsData.companies || [];
          const locations = settingsData.locations || [];

          console.log('✅ Real-time company settings loaded:', {
            companies: companies.length,
            locations: locations.length
          });

          setCompanySettings({
            companies,
            locations,
            companyDetails: settingsData.companyDetails || {}
          });

          // Also update localStorage for offline access
          const storageKey = `companySettings_${farmId}`;
          const dataWrapper = {
            data: {
              companies,
              locations,
              defaultCompany: settingsData.defaultCompany || '',
              defaultLocation: settingsData.defaultLocation || '',
              companyDetails: settingsData.companyDetails || {}
            },
            lastModified: new Date().toISOString(),
            synced: true,
            farmId: farmId,
            dataType: 'companySettings'
          };
          localStorage.setItem(storageKey, JSON.stringify(dataWrapper));
          console.log('💾 Updated localStorage with real-time company settings');
        } else {
          console.log('📄 No company settings document found');
          setCompanySettings({
            companies: [],
            locations: []
          });
        }

        setLoading(false);
      },
      (error) => {
        console.error('❌ Real-time company settings listener error:', error);
        setLoading(false);

        // Fallback to localStorage
        try {
          const storageKey = `companySettings_${farmId}`;
          const localData = localStorage.getItem(storageKey);
          if (localData) {
            const parsed = JSON.parse(localData);
            const actualData = parsed.data || parsed;
            setCompanySettings({
              companies: actualData.companies || [],
              locations: actualData.locations || []
            });
            console.log('📱 Fallback to localStorage company settings');
          }
        } catch (fallbackError) {
          console.error('❌ Fallback to localStorage failed:', fallbackError);
        }
      }
    );

    return () => {
      console.log('🔄 Cleaning up real-time company settings listener');
      unsubscribe();
    };
  }, [farmId]);

  return { companySettings, loading };
};

// Romanian CNP birth date extraction function
const extractBirthDataFromCNP = (cnp) => {
  if (!cnp || cnp.length !== 13) {
    return { isValid: false };
  }

  // Validate CNP format (13 digits)
  if (!/^\d{13}$/.test(cnp)) {
    return { isValid: false };
  }

  // Extract components
  const s = parseInt(cnp[0]); // Sex and century
  const yy = cnp.substring(1, 3); // Year (last 2 digits)
  const mm = cnp.substring(3, 5); // Month
  const dd = cnp.substring(5, 7); // Day
  const jj = cnp.substring(7, 9); // County code

  // Validate first digit (sex and century)
  if (![1, 2, 3, 4, 5, 6, 7, 8, 9].includes(s)) {
    return { isValid: false };
  }

  // Determine century and sex
  let century;
  let sex;
  switch (s) {
    case 1: century = 1900; sex = 'M'; break;
    case 2: century = 1900; sex = 'F'; break;
    case 3: century = 1800; sex = 'M'; break;
    case 4: century = 1800; sex = 'F'; break;
    case 5: century = 2000; sex = 'M'; break;
    case 6: century = 2000; sex = 'F'; break;
    case 7: century = 1900; sex = 'M'; break; // Resident
    case 8: century = 1900; sex = 'F'; break; // Resident
    case 9: century = 1900; sex = 'M'; break; // Foreign
    default: return { isValid: false };
  }

  // Calculate full year
  const fullYear = century + parseInt(yy);

  // Validate month and day
  const month = parseInt(mm);
  const day = parseInt(dd);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return { isValid: false };
  }

  // Validate CNP checksum
  const controlKey = "279146358279";
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnp[i]) * parseInt(controlKey[i]);
  }
  const remainder = sum % 11;
  const controlDigit = remainder === 10 ? 1 : remainder;

  if (parseInt(cnp[12]) !== controlDigit) {
    return { isValid: false };
  }

  // County mapping for birth location
  const countyMap = {
    '01': 'Alba', '02': 'Arad', '03': 'Argeș', '04': 'Bacău', '05': 'Bihor',
    '06': 'Bistrița-Năsăud', '07': 'Botoșani', '08': 'Brașov', '09': 'Brăila',
    '10': 'Buzău', '11': 'Caraș-Severin', '12': 'Cluj', '13': 'Constanța',
    '14': 'Covasna', '15': 'Dâmbovița', '16': 'Dolj', '17': 'Galați',
    '18': 'Gorj', '19': 'Harghita', '20': 'Hunedoara', '21': 'Ialomița',
    '22': 'Iași', '23': 'Ilfov', '24': 'Maramureș', '25': 'Mehedinți',
    '26': 'Mureș', '27': 'Neamț', '28': 'Olt', '29': 'Prahova',
    '30': 'Satu Mare', '31': 'Sălaj', '32': 'Sibiu', '33': 'Suceava',
    '34': 'Teleorman', '35': 'Timiș', '36': 'Tulcea', '37': 'Vaslui',
    '38': 'Vâlcea', '39': 'Vrancea', '40': 'București', '41': 'București Sector 1',
    '42': 'București Sector 2', '43': 'București Sector 3', '44': 'București Sector 4',
    '45': 'București Sector 5', '46': 'București Sector 6', '47': 'București Sector 7',
    '48': 'București Sector 8', '51': 'Călărași', '52': 'Giurgiu'
  };

  const birthCounty = countyMap[jj] || 'Necunoscut';

  return {
    isValid: true,
    birthDate: `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${fullYear}`,
    birthYear: fullYear,
    birthMonth: month,
    birthDay: day,
    birthCounty: birthCounty,
    sex: sex
  };
};

// ── Screen helpers ───────────────────────────────────────────────────────────

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const emptyParcelRow = () => ({
  key: uid(), parcela: '', suprafata: '', cadastralNumber: '', categorie: 'arabil',
  nord: '', est: '', sud: '', vest: '', mark: '',
});
const emptyTGroup = (locatie = '') => ({ key: uid(), t: '', locatie, parcels: [emptyParcelRow()] });

// A T number only means something inside its comună: T1 can exist in several communes.
const tKeyOf = (t, locatie) => `${t}|||${locatie || ''}`;
const splitTKey = (key) => { const [t, locatie] = String(key).split('|||'); return { t: parseInt(t, 10), locatie: locatie || '' }; };
const sortTKeys = (keys) => [...keys].sort((a, b) => {
  const x = splitTKey(a); const y = splitTKey(b);
  return x.t - y.t || x.locatie.localeCompare(y.locatie);
});

const CATEGORY_LABEL = { arabil: 'Arabil', fanete: 'Fânețe', pasune: 'Pășune' };

const ParcelLine = ({ parcel }) => (
  <div className="pm-parcel-line">
    <div>
      <strong>Parcela {parcel.parcela || 'N/A'}</strong>
      <span>{formatHa(parcel.suprafata)} ha · {CATEGORY_LABEL[parcel.categorie] || parcel.categorie || 'Arabil'}{parcel.cadastralNumber && parcel.cadastralNumber !== '-' ? ` · Nr. cadastral ${parcel.cadastralNumber}` : ''}</span>
      {[parcel.nord, parcel.est, parcel.sud, parcel.vest].some((v) => v && v !== '-') && (
        <span>N: {parcel.nord || '—'} · E: {parcel.est || '—'} · S: {parcel.sud || '—'} · V: {parcel.vest || '—'}</span>
      )}
      {parcel.dateAdded && <span>Adăugată {formatDate(parcel.dateAdded)}</span>}
    </div>
  </div>
);

const ParcelManager = ({ userRole, testStore }) => {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    nume: '',
    cnp: '',
    seria: '',
    numarulBuletinului: '',
    titluriProprietate: [{ numar: '', data: '' }], // Multiple titlu de proprietate
    mentiuni: '', // Mentiuni field
    domiciliat: '',
    dataNasterii: '',
    localitateNastere: '',
    judetNastere: '',
    firma: '',
    locatie: '',
    dataContract: '',
    numarContract: '',
    contracteAditionale: []
  });
  const [view, setView] = useState('view');
  const [showAdd, setShowAdd] = useState(false);
  const [addStep, setAddStep] = useState(0);
  const [tGroups, setTGroups] = useState(() => [emptyTGroup()]);
  const [cadastralChoice, setCadastralChoice] = useState(null);
  const [newTPrompt, setNewTPrompt] = useState(null);
  const toastTimer = useRef(null);
  const [filters, setFilters] = useState({
    t: '',
    location: '',
    company: '',
    name: '',
    titluProprietate: '',
    sortBy: 'alfabetic',
    hasCadastral: false
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editValidationErrors, setEditValidationErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Get farmId from authentication
  const currentSession = firebaseAuthService.getCurrentSession();
  const farmId = testStore ? null : currentSession?.farm?.id;

  // Real-time parcels data with Firebase sync
  const { parcels: realtimeParcels, loading: parcelsLoading, error: parcelsError } = useRealtimeParcels(farmId);

  // Real-time company settings with Firebase sync
  const { companySettings, loading: settingsLoading } = useRealtimeCompanySettings(farmId);

  // Extract companies and locations from real-time data
  const companies = testStore ? testStore.companies : companySettings.companies;
  const locations = testStore ? testStore.locations : companySettings.locations;
  const companyNames = useMemo(() => (companies || []).map(nameOf).filter(Boolean), [companies]);
  const locationNames = useMemo(() => (locations || []).map(nameOf).filter((name) => name && !isVillageName(name)), [locations]);

  // Update local parcels state when real-time data changes
  useEffect(() => {
    if (testStore) {
      setParcels(testStore.parcels);
      setLoading(false);
      return;
    }
    if (realtimeParcels) {
      setParcels(realtimeParcels);
    }
    setLoading(parcelsLoading || settingsLoading);
  }, [realtimeParcels, parcelsLoading, settingsLoading, testStore]);

  // One-time migration: rename 'iugani' / 'Iugani' → 'Mircesti' in locatie field
  const iuganiMigrationDone = React.useRef(false);
  useEffect(() => {
    if (iuganiMigrationDone.current || !parcels || parcels.length === 0) return;
    const hasIugani = parcels.some(p => p.locatie && p.locatie.toLowerCase() === 'iugani');
    if (!hasIugani) { iuganiMigrationDone.current = true; return; }
    iuganiMigrationDone.current = true;
    const migrated = parcels.map(p => ({
      ...p,
      locatie: (p.locatie && p.locatie.toLowerCase() === 'iugani') ? 'Mircesti' : p.locatie
    }));
    setParcels(migrated);
    saveData(migrated).then(() => {
      console.log('✅ Migration: changed iugani → Mircesti in parcel data');
    }).catch(err => {
      console.error('Migration save failed:', err);
      iuganiMigrationDone.current = false;
    });
  }, [parcels]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      showToast('Conexiune restabilită. Datele se sincronizează.', 'success');
    };

    const handleOffline = () => {
      setIsOnline(false);
      showToast('Ești offline. Modificările se salvează local.', 'warning');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Monitor Firebase connection status
  useEffect(() => {
    const checkFirebaseConnection = async () => {
      if (!isOnline || testStore) return;

      try {
        // Try to perform a simple Firebase operation to check connectivity
        const testRef = doc(db, 'connectionTest', 'test');
        await setDoc(testRef, { timestamp: new Date() }, { merge: true });
        console.log('🔥 Firebase connection confirmed');
      } catch (error) {
        console.warn('⚠️ Firebase connection issue:', error);
        showToast('⚠️ Probleme de conexiune cu serverul - datele se salvează local', 'warning');
      }
    };

    if (isOnline) {
      checkFirebaseConnection();
    }
  }, [isOnline]);

  // Manual refresh function for companies and locations - now just shows status
  const refreshCompaniesFromSettings = () => {
    showToast(`🔄 Sincronizare automată activă! Găsite ${companies?.length || 0} firme și ${locations?.length || 0} locații`, 'info');
  };

  // Navigate to Settings
  const goToSettings = () => {
    showToast('💡 Mergi la Settings pentru a configura firmele și locațiile', 'info');
  };

  const saveData = async (data = parcels) => {
    if (testStore) { testStore.parcels = data; testStore.saves += 1; return; }
    if (!farmId) return;

    try {
      setIsSaving(true);

      if (!isOnline) {
        // Save to localStorage when offline
        const storageKey = `parcelsData_${farmId}`;
        const dataWrapper = {
          data,
          lastModified: new Date().toISOString(),
          synced: false, // Mark as not synced
          farmId: farmId,
          dataType: 'parcelsData'
        };
        localStorage.setItem(storageKey, JSON.stringify(dataWrapper));
        showToast('📱 Salvat local - se va sincroniza când revine conexiunea', 'warning');
        return;
      }

      // Save using real-time Firebase sync - all devices will update automatically!
      await saveRealtimeParcels(farmId, data);

      // No artificial delay - Firebase save is instant feedback
    } catch (error) {
      console.error('Error saving data:', error);

      // Fallback to localStorage on Firebase error
      try {
        const storageKey = `parcelsData_${farmId}`;
        const dataWrapper = {
          data,
          lastModified: new Date().toISOString(),
          synced: false,
          farmId: farmId,
          dataType: 'parcelsData'
        };
        localStorage.setItem(storageKey, JSON.stringify(dataWrapper));
        showToast('⚠️ Salvat local datorită problemelor de conexiune', 'warning');
      } catch (localError) {
        showToast('❌ Eroare la salvarea datelor!', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const showToast = (message, type = 'info') => {
    clearTimeout(toastTimer.current);
    setToast({ show: true, message, type });
    toastTimer.current = setTimeout(() => setToast((prev) => ({ ...prev, show: false })), type === 'error' ? 5000 : 3000);
  };

  // Handle additional contracts
  const addAdditionalContract = () => {
    setFormData(prev => ({
      ...prev,
      contracteAditionale: [...prev.contracteAditionale, {
        dataContract: '',
        descriere: '',
        numarContract: '',
        firma: '',
        locatie: ''
      }]
    }));
  };

  const removeAdditionalContract = (index) => {
    setFormData(prev => ({
      ...prev,
      contracteAditionale: prev.contracteAditionale.filter((_, i) => i !== index)
    }));
  };

  const updateAdditionalContract = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      contracteAditionale: prev.contracteAditionale.map((contract, i) =>
        i === index ? { ...contract, [field]: value } : contract
      )
    }));
  };

  // Handle multiple titlu de proprietate
  const addTitluProprietate = () => {
    setFormData(prev => ({
      ...prev,
      titluriProprietate: [...prev.titluriProprietate, { numar: '', data: '' }]
    }));
  };

  const removeTitluProprietate = (index) => {
    if (formData.titluriProprietate.length > 1) {
      setFormData(prev => ({
        ...prev,
        titluriProprietate: prev.titluriProprietate.filter((_, i) => i !== index)
      }));
    }
  };

  const updateTitluProprietate = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      titluriProprietate: prev.titluriProprietate.map((titlu, i) =>
        i === index ? { ...titlu, [field]: value } : titlu
      )
    }));
  };

  // ── Adding parcels: the T groups and parcels being typed ─────────────────────

  const addTParcelGroup = () => setTGroups((groups) => [...groups, emptyTGroup(formData.locatie)]);
  const removeTGroup = (groupKey) => setTGroups((groups) => groups.filter((g) => g.key !== groupKey));

  const updateTNumber = (groupKey, value) => setTGroups((groups) => groups.map((g) => (
    // The cadastral number depends on the T, so it's looked up again after a change.
    g.key !== groupKey ? g : { ...g, t: value, parcels: g.parcels.map((p) => ({ ...p, cadastralNumber: '', mark: '' })) }
  )));

  const updateTLocatie = (groupKey, value) => setTGroups((groups) => groups.map((g) => (
    // The same T in another comună is different land, so the cadastral number is looked up again.
    g.key !== groupKey ? g : { ...g, locatie: value, parcels: g.parcels.map((p) => ({ ...p, cadastralNumber: '', mark: '' })) }
  )));

  const addParcelInput = (groupKey) => setTGroups((groups) => groups.map((g) => (
    g.key !== groupKey ? g : { ...g, parcels: [...g.parcels, emptyParcelRow()] }
  )));

  const removeParcelInput = (groupKey, parcelKey) => setTGroups((groups) => groups.map((g) => (
    g.key !== groupKey ? g : { ...g, parcels: g.parcels.filter((p) => p.key !== parcelKey) }
  )));

  const updateParcelInput = (groupKey, parcelKey, changes) => setTGroups((groups) => groups.map((g) => (
    g.key !== groupKey ? g : { ...g, parcels: g.parcels.map((p) => (p.key === parcelKey ? { ...p, ...changes } : p)) }
  )));

  const updateOwnerName = (value) => {
    const capitalizedName = value.toLowerCase().split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    setFormData((prev) => ({ ...prev, nume: capitalizedName }));
    if (validationErrors.nume) setValidationErrors((prev) => ({ ...prev, nume: false }));
    setTGroups((groups) => groups.map((g) => ({ ...g, parcels: g.parcels.map((p) => ({ ...p, cadastralNumber: '', mark: '' })) })));
  };

  // Looks up the cadastral number once T, owner and area are known.
  const tryAutocomplete = (group, row) => {
    const surface = parseFloat(row.suprafata);
    const ownerName = formData.nume;
    if (!group.t || !ownerName?.trim() || !surface || surface <= 0) return;

    cadastralService.refresh();
    const result = cadastralService.findCadastralNumber(`T${group.t}`, ownerName, surface, group.locatie || formData.locatie || null);
    if (!result) return;

    if (result.multipleMatches) {
      setCadastralChoice({
        title: `${result.matches.length} parcele pentru ${result.ownerName}`,
        message: `Ai introdus ${surface} ha. Alege parcela potrivită.`,
        options: result.matches.map((match) => ({
          key: String(match.cadastralNumber), cadastralNumber: match.cadastralNumber,
          surface: match.storedSurface, address: match.address, exact: match.surfaceMatch,
        })),
        groupKey: group.key, parcelKey: row.key,
      });
    } else if (result.autoFill) {
      updateParcelInput(group.key, row.key, { cadastralNumber: result.cadastralNumber, mark: 'exact' });
      showToast(result.message, 'success');
    } else {
      setCadastralChoice({
        title: 'Verifică numărul cadastral', message: result.message,
        options: [{ key: String(result.cadastralNumber), cadastralNumber: result.cadastralNumber, exact: false }],
        groupKey: group.key, parcelKey: row.key,
      });
    }
  };

  const chooseCadastral = (option) => {
    updateParcelInput(cadastralChoice.groupKey, cadastralChoice.parcelKey, {
      cadastralNumber: option.cadastralNumber, mark: option.exact ? 'exact' : 'check',
    });
    setCadastralChoice(null);
  };

  const validateAddStep = (step) => {
    const errors = {};
    if (step === 0) {
      ['nume', 'cnp', 'domiciliat', 'dataNasterii', 'localitateNastere', 'judetNastere', 'seria', 'numarulBuletinului']
        .forEach((field) => { if (!formData[field]) errors[field] = true; });
      if (formData.cnp && !/^\d{13}$/.test(formData.cnp)) errors.cnp = true;
      if (formData.numarulBuletinului && !/^\d{6}$/.test(formData.numarulBuletinului)) errors.numarulBuletinului = true;
    }
    if (step === 1 && !formData.titluriProprietate.some((titlu) => titlu.numar && titlu.numar.trim())) {
      errors.titluriProprietate = true;
    }
    if (step === 2) {
      ['firma', 'locatie', 'dataContract', 'numarContract'].forEach((field) => { if (!formData[field]) errors[field] = true; });
      formData.contracteAditionale.forEach((contract, index) => {
        if (!contract.dataContract || !contract.firma || !contract.locatie) errors[`contract_${index}`] = true;
      });
    }
    if (step === 3) {
      if (tGroups.length === 0) errors.tGroups = true;
      tGroups.forEach((group) => {
        if (!group.t) errors[`t_${group.key}`] = true;
        if (!group.locatie) errors[`l_${group.key}`] = true;
        if (group.parcels.length === 0) errors[`t_${group.key}`] = true;
        group.parcels.forEach((row) => {
          if (!parseFloat(row.suprafata) || parseFloat(row.suprafata) <= 0) errors[`s_${row.key}`] = true;
        });
      });
    }
    return errors;
  };

  const ADD_STEP_MESSAGE = [
    'Completează câmpurile marcate. CNP-ul are 13 cifre, iar numărul buletinului 6 cifre.',
    'Adaugă cel puțin un titlu de proprietate.',
    'Completează câmpurile marcate.',
    'Fiecare T are nevoie de un număr și o comună, iar fiecare parcelă de o suprafață.',
  ];

  const nextAddStep = () => {
    const errors = validateAddStep(addStep);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) { showToast(ADD_STEP_MESSAGE[addStep], 'error'); return; }
    // T groups start in the contract's comună, and can be changed one by one.
    if (addStep === 2) setTGroups((groups) => groups.map((g) => (g.locatie ? g : { ...g, locatie: formData.locatie })));
    setAddStep((step) => step + 1);
  };

  const resetAddForm = () => {
    setFormData({
      nume: '', cnp: '', seria: '', numarulBuletinului: '',
      titluriProprietate: [{ numar: '', data: '' }], mentiuni: '',
      domiciliat: '', dataNasterii: '', localitateNastere: '', judetNastere: '',
      firma: companyNames[0] || 'PEPA AGRO SRL',
      locatie: locationNames[0] || 'Mircești',
      dataContract: '', numarContract: '', contracteAditionale: [],
    });
    setTGroups([emptyTGroup(locationNames[0] || 'Mircești')]);
    setValidationErrors({});
    setAddStep(0);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    for (let step = 0; step < 4; step++) {
      const errors = validateAddStep(step);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setAddStep(step);
        showToast(ADD_STEP_MESSAGE[step], 'error');
        return;
      }
    }

    const firstValidTitlu = formData.titluriProprietate.find((titlu) => titlu.numar && titlu.numar.trim());
    const parcelsToAdd = [];
    tGroups.forEach((group) => {
      group.parcels.forEach((row) => {
        parcelsToAdd.push({
          id: Date.now() + Math.random(),
          t: parseInt(group.t, 10),
          nume: formData.nume,
          cnp: formData.cnp,
          seria: formData.seria,
          numarulBuletinului: formData.numarulBuletinului,
          numarTitluProprietate: firstValidTitlu ? firstValidTitlu.numar : '',
          titluriProprietate: formData.titluriProprietate,
          mentiuni: formData.mentiuni,
          domiciliat: formData.domiciliat,
          dataNasterii: formData.dataNasterii,
          localitateNastere: formData.localitateNastere || '',
          judetNastere: formData.judetNastere,
          firma: formData.firma,
          locatie: group.locatie,
          parcela: row.parcela.trim() || '',
          suprafata: parseFloat(row.suprafata),
          cadastralNumber: row.cadastralNumber.trim() || '',
          categorie: row.categorie || 'arabil',
          nord: row.nord.trim(),
          est: row.est.trim(),
          sud: row.sud.trim(),
          vest: row.vest.trim(),
          dataContract: formData.dataContract,
          numarContract: formData.numarContract,
          contracteAditionale: formData.contracteAditionale,
          dateAdded: new Date().toISOString(),
        });
      });
    });

    const newParcels = [...parcels, ...parcelsToAdd];
    setParcels(newParcels);
    await saveData(newParcels);
    showToast(`${parcelsToAdd.length} parcele adăugate pentru ${formData.nume}.`, 'success');
    resetAddForm();
    setShowAdd(false);
  };

  const getGroupedParcels = () => {
    try {
      if (!Array.isArray(parcels) || parcels.length === 0) {
        return [];
      }

      // Group by T number, then by person within each T
      const grouped = {};

      parcels.forEach(parcel => {
        if (!parcel || !parcel.t || !parcel.nume) return; // Skip invalid parcels

        // Group by T number first, then by person within each T
        const tKey = `T${parcel.t}_${parcel.locatie || 'N/A'}`;
        const personKey = `${parcel.nume}_${parcel.cnp}_${parcel.firma || 'N/A'}`;
        const fullKey = `${tKey}_${personKey}`;

        if (!grouped[fullKey]) {
          grouped[fullKey] = {
            t: parcel.t,
            nume: parcel.nume || 'N/A',
            cnp: parcel.cnp || 'N/A',
            firma: parcel.firma || 'N/A',
            locatie: parcel.locatie || 'N/A',
            dataContract: parcel.dataContract || 'N/A',
            numarContract: parcel.numarContract || 'N/A',
            parcele: [],
            totalSuprafata: 0
          };
        }

        // Add this parcel to the group
        grouped[fullKey].parcele.push({
          parcela: parcel.parcela || 'N/A',
          suprafata: parseFloat(parcel.suprafata) || 0,
          dateAdded: parcel.dateAdded
        });
        grouped[fullKey].totalSuprafata += parseFloat(parcel.suprafata) || 0;
      });

      // Apply filters
      let filteredGroups = Object.values(grouped);

      if (filters.t) {
        filteredGroups = filteredGroups.filter(group => Number(group.t) === parseInt(filters.t, 10));
      }
      if (filters.location) {
        filteredGroups = filteredGroups.filter(group => group.locatie === filters.location);
      }
      if (filters.company) {
        filteredGroups = filteredGroups.filter(group => group.firma === filters.company);
      }
      if (filters.name) {
        const wanted = filters.name.trim().toLowerCase();
        filteredGroups = filteredGroups.filter(group =>
          group.nume.toLowerCase().includes(wanted) || String(group.cnp).includes(wanted)
        );
      }
      if (filters.titluProprietate) {
        const wanted = filters.titluProprietate.toLowerCase();
        filteredGroups = filteredGroups.filter(group => parcels.some(p => (
          Number(p.t) === Number(group.t) && (p.locatie || 'N/A') === group.locatie && p.nume === group.nume && p.cnp === group.cnp
          && (p.numarTitluProprietate || '').toLowerCase().includes(wanted)
        )));
      }
      if (filters.hasCadastral) {
        filteredGroups = filteredGroups.filter(group => {
          // Check if any parcel in this group has a cadastral number
          const groupParcels = parcels.filter(p =>
            Number(p.t) === Number(group.t) && (p.locatie || 'N/A') === group.locatie && p.nume === group.nume && p.cnp === group.cnp
          );
          return groupParcels.some(p => p.cadastralNumber && p.cadastralNumber.trim() !== '');
        });
      }

      // Sort by T number first, then by name
      filteredGroups.sort((a, b) => {
        if (Number(a.t) !== Number(b.t)) return Number(a.t) - Number(b.t);
        return a.nume.localeCompare(b.nume);
      });

      return filteredGroups;
    } catch (error) {
      console.error('Error in getGroupedParcels:', error);
      return [];
    }
  };

  const getPeopleData = () => {
    try {
      if (!Array.isArray(parcels) || parcels.length === 0) {
        return [];
      }

      const peopleData = {};

      parcels.forEach(parcel => {
        if (!parcel || !parcel.cnp) return; // Skip invalid parcels

        if (!peopleData[parcel.cnp]) {
          peopleData[parcel.cnp] = {
            nume: parcel.nume || 'N/A',
            cnp: parcel.cnp || 'N/A',
            seria: parcel.seria || 'N/A',
            numarulBuletinului: parcel.numarulBuletinului || 'N/A',
            numarTitluProprietate: parcel.numarTitluProprietate || 'N/A',
            locatie: parcel.locatie || 'N/A',
            dataContract: parcel.dataContract || 'N/A',
            parcels: [],
            totalSuprafata: 0,
            tNumbers: new Set(),
            totalParcele: 0
          };
        }

        peopleData[parcel.cnp].parcels.push(parcel);
        peopleData[parcel.cnp].totalSuprafata += parseFloat(parcel.suprafata) || 0;
        if (parcel.t) {
          peopleData[parcel.cnp].tNumbers.add(parcel.t);
        }
        peopleData[parcel.cnp].totalParcele++;
      });

      let peopleArray = Object.values(peopleData);

      // Apply filters with null checks
      if (filters.location) {
        peopleArray = peopleArray.filter(person => person.parcels && person.parcels.some(parcel => parcel && parcel.locatie === filters.location));
      }
      if (filters.company) {
        peopleArray = peopleArray.filter(person =>
          person.parcels && person.parcels.some(parcel => parcel && parcel.firma === filters.company)
        );
      }
      if (filters.name) {
        const wanted = filters.name.trim().toLowerCase();
        peopleArray = peopleArray.filter(person =>
          (person.nume && person.nume.toLowerCase().includes(wanted)) || String(person.cnp || '').includes(wanted)
        );
      }
      if (filters.titluProprietate) {
        peopleArray = peopleArray.filter(person =>
          person.numarTitluProprietate && person.numarTitluProprietate.toLowerCase().includes(filters.titluProprietate.toLowerCase())
        );
      }
      if (filters.hasCadastral) {
        peopleArray = peopleArray.filter(person =>
          person.parcels && person.parcels.some(parcel =>
            parcel.cadastralNumber && parcel.cadastralNumber.trim() !== ''
          )
        );
      }

      // Sort with null checks
      peopleArray.sort((a, b) => {
        switch (filters.sortBy) {
          case 'nume':
          case 'alfabetic':
            return (a.nume || '').localeCompare(b.nume || '');
          case 'suprafata_mare_mic':
            return (b.totalSuprafata || 0) - (a.totalSuprafata || 0);
          case 'suprafata_mic_mare':
            return (a.totalSuprafata || 0) - (b.totalSuprafata || 0);
          case 'dataContract':
            const aDate = a.dataContract ? new Date(a.dataContract) : new Date(0);
            const bDate = b.dataContract ? new Date(b.dataContract) : new Date(0);
            return aDate - bDate;
          case 'expirare':
            const aExpire = a.dataContract ? new Date(a.dataContract) : new Date(0);
            const bExpire = b.dataContract ? new Date(b.dataContract) : new Date(0);
            aExpire.setFullYear(aExpire.getFullYear() + 10);
            bExpire.setFullYear(bExpire.getFullYear() + 10);
            return aExpire - bExpire;
          default:
            return (a.nume || '').localeCompare(b.nume || '');
        }
      });

      return peopleArray;
    } catch (error) {
      console.error('Error in getPeopleData:', error);
      return [];
    }
  };

  const exportToExcel = () => {
    if (parcels.length === 0) {
      showToast('Nu există date pentru export!', 'error');
      return;
    }

    const exportData = (parcels || []).map(parcel => ({
      'T': `T${parcel.t}`,
      'Nume': parcel.nume,
      'CNP': parcel.cnp,
      'Locație': parcel.locatie,
      'Parcela': parcel.parcela || 'N/A',
      'Suprafață (ha)': formatHa(parcel.suprafata),
      'Nr. Cadastral': parcel.cadastralNumber || '-',
      'Categorie de folosință': parcel.categorie || 'arabil',
      'Data Contract': formatDate(parcel.dataContract)
    }));

    const wb = window.XLSX.utils.book_new();
    const ws = window.XLSX.utils.json_to_sheet(exportData);

    ws['!cols'] = [
      { width: 10 }, { width: 25 }, { width: 15 }, { width: 15 },
      { width: 15 }, { width: 15 }, { width: 15 }, { width: 18 }, { width: 15 }
    ];

    window.XLSX.utils.book_append_sheet(wb, ws, 'Parcels');

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const filename = `Parcels_${dateStr}.xlsx`;

    window.XLSX.writeFile(wb, filename);
    showToast(`Fișierul Excel a fost exportat: ${filename}`, 'success');
  };

  const inGroup = (parcel, group) => parcel.t == group.t && (parcel.locatie || 'N/A') === group.locatie && parcel.nume === group.nume && parcel.cnp === group.cnp; // eslint-disable-line eqeqeq

  const showParcelsDetails = (group) => {
    const groupParcels = parcels.filter((p) => inGroup(p, group));
    setDetailsData({
      group,
      parcels: groupParcels,
      totalArea: groupParcels.reduce((sum, p) => sum + toNumber(p.suprafata), 0),
      type: 'group',
    });
    setShowDetailsModal(true);
  };

  const showPersonDetails = (cnp) => {
    const personParcels = parcels.filter(p => p.cnp === cnp);
    const person = personParcels[0];

    // Group parcels by T
    const groupedByT = {};
    personParcels.forEach(parcel => {
      const key = tKeyOf(Number(parcel.t), parcel.locatie);
      if (!groupedByT[key]) {
        groupedByT[key] = [];
      }
      groupedByT[key].push(parcel);
    });

    setDetailsData({
      title: `Toate parcelele pentru ${person.nume}`,
      person: person,
      groupedByT: groupedByT,
      type: 'person'
    });
    setShowDetailsModal(true);
  };

  const deleteGroup = async (group) => {
    const { t, nume } = group;
    if (window.confirm(`Sunteți sigur că doriți să ștergeți toate parcelele pentru ${nume} din T${t}, ${group.locatie}?`)) {
      const parcelsToDelete = parcels.filter((p) => inGroup(p, group));

      if (!testStore) {
        // Fast backup: Move from farmData to farmBackup (non-blocking)
        backupService.backupBeforeDeletion('parcelsData', parcelsToDelete, 'group_delete', {
          groupKey: `${t}-${group.locatie}-${nume}-${group.cnp}`,
          deletedBy: 'user',
          totalParcels: parcelsToDelete.length,
          totalSurface: parcelsToDelete.reduce((sum, p) => sum + toNumber(p.suprafata), 0),
        });
      }

      const newParcels = parcels.filter((p) => !inGroup(p, group));
      setParcels(newParcels);
      await saveData(newParcels);
      showToast(`Parcelele pentru ${nume} din T${t}, ${group.locatie}, au fost șterse.`, 'success');
    }
  };

  // Pre-select company and commune without overwriting what's already chosen,
  // because a sync update can arrive while the form is being filled in.
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      firma: prev.firma || companyNames[0] || '',
      locatie: prev.locatie || locationNames[0] || '',
    }));
  }, [companyNames, locationNames]);

  const editPerson = (cnp) => {
    const personParcels = parcels.filter(p => p.cnp === cnp);
    if (personParcels.length === 0) {
      showToast('Nu s-au găsit parcele pentru această persoană', 'error');
      return;
    }

    const person = personParcels[0]; // Get person data from first parcel

    // Group parcels by T number for editing
    const groupedParcels = {};
    personParcels.forEach(parcel => {
      const key = tKeyOf(Number(parcel.t), parcel.locatie);
      if (!groupedParcels[key]) {
        groupedParcels[key] = [];
      }
      groupedParcels[key].push({ ...parcel });
    });

    // Auto-fill data from CNP if birth data is missing
    let autoFilledData = {};
    if (person.cnp && person.cnp.length === 13) {
      const birthData = extractBirthDataFromCNP(person.cnp);
      if (birthData.isValid) {
        const birthDate = `${birthData.birthYear}-${birthData.birthMonth.toString().padStart(2, '0')}-${birthData.birthDay.toString().padStart(2, '0')}`;
        autoFilledData = {
          dataNasterii: person.dataNasterii || birthDate,
          judetNastere: person.judetNastere || birthData.birthCounty
        };

        // Show feedback if we auto-filled missing data
        if (!person.dataNasterii || !person.judetNastere) {
          setTimeout(() => {
            showToast(`🔄 Auto-completat din CNP: ${!person.dataNasterii ? 'Data nașterii' : ''} ${!person.judetNastere ? 'Județul nașterii' : ''}`, 'info');
          }, 500);
        }
      }
    }

    // Pre-fill the edit form with existing data + auto-filled data
    setEditFormData({
      originalCnp: cnp, // Keep track of original CNP for updates
      nume: person.nume || '',
      cnp: person.cnp || '',
      seria: person.seria || '',
      numarulBuletinului: person.numarulBuletinului || '',
      numarTitluProprietate: person.numarTitluProprietate || '',
      titluriProprietate: person.titluriProprietate || [{ numar: person.numarTitluProprietate || '', data: '' }], // Multiple titlu fields
      mentiuni: person.mentiuni || '', // Mentiuni field
      domiciliat: person.domiciliat || '',
      dataNasterii: autoFilledData.dataNasterii || person.dataNasterii || '',
      localitateNastere: person.localitateNastere || '',
      judetNastere: autoFilledData.judetNastere || person.judetNastere || '',
      firma: person.firma || companyNames[0] || 'PEPA AGRO SRL',
      locatie: person.locatie || locationNames[0] || 'Mircești',
      dataContract: person.dataContract || '',
      numarContract: person.numarContract || '',
      contracteAditionale: person.contracteAditionale || [],
      // Add parcels data for editing
      groupedParcels: groupedParcels,
      personParcels: personParcels
    });

    setEditValidationErrors({});
    setShowEditModal(true);
  };

  // Handle CNP auto-completion in edit modal
  const handleEditCNPChange = (newCNP) => {
    handleEditInputChange('cnp', newCNP);
    if (newCNP && newCNP.length === 13) {
      const birthData = extractBirthDataFromCNP(newCNP);
      if (birthData.isValid) {
        handleEditInputChange('dataNasterii', birthData.birthDate);
        handleEditInputChange('judetNastere', birthData.birthCounty);
        showToast(`CNP valid. Născut/ă ${birthData.birthDate}, județul ${birthData.birthCounty}.`, 'success');
      } else {
        showToast('CNP invalid. Verifică cele 13 cifre.', 'warning');
      }
    }
  };

  // Handle parcel updates in edit modal
  const handleParcelUpdate = (tNumber, parcelIndex, field, value) => {
    const updatedGroupedParcels = { ...editFormData.groupedParcels };
    if (updatedGroupedParcels[tNumber] && updatedGroupedParcels[tNumber][parcelIndex]) {
      updatedGroupedParcels[tNumber] = [...updatedGroupedParcels[tNumber]];
      updatedGroupedParcels[tNumber][parcelIndex] = {
        ...updatedGroupedParcels[tNumber][parcelIndex],
        [field]: value
      };

      // Also update the personParcels array
      const updatedPersonParcels = editFormData.personParcels.map(parcel => {
        if (parcel.id === updatedGroupedParcels[tNumber][parcelIndex].id) {
          return updatedGroupedParcels[tNumber][parcelIndex];
        }
        return parcel;
      });

      setEditFormData(prev => ({
        ...prev,
        groupedParcels: updatedGroupedParcels,
        personParcels: updatedPersonParcels
      }));
    }
  };

  // Add new parcel to person in edit modal
  const addParcelToEditPerson = (groupKey) => {
    const { t, locatie } = splitTKey(groupKey);
    const newParcel = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      t,
      locatie,
      parcela: '',
      suprafata: '',
      cadastralNumber: '',
      categorie: 'arabil',
      nord: '',
      est: '',
      sud: '',
      vest: '',
      dateAdded: new Date().toISOString(),
    };
    setEditFormData((prev) => ({
      ...prev,
      groupedParcels: { ...prev.groupedParcels, [groupKey]: [...(prev.groupedParcels[groupKey] || []), newParcel] },
    }));
  };

  // Moves a T group to another comună; it joins that comună's group if the T is already there.
  const changeEditGroupLocatie = (groupKey, locatie) => {
    const { t } = splitTKey(groupKey);
    const targetKey = tKeyOf(t, locatie);
    if (targetKey === groupKey) return;
    setEditFormData((prev) => {
      const moved = (prev.groupedParcels[groupKey] || []).map((parcel) => ({ ...parcel, locatie }));
      const next = { ...prev.groupedParcels };
      delete next[groupKey];
      next[targetKey] = [...(next[targetKey] || []), ...moved];
      return { ...prev, groupedParcels: next };
    });
  };

  // Delete parcel from person in edit modal
  const deleteParcelFromEditPerson = (groupKey, parcelIndex) => {
    const { t, locatie } = splitTKey(groupKey);
    const parcelToDelete = editFormData.groupedParcels[groupKey][parcelIndex];
    const confirmMessage = `Ștergeți această parcelă?\n\nParcela: ${parcelToDelete.parcela || 'N/A'}\nSuprafață: ${parcelToDelete.suprafata || 0} ha\nT${t}, ${locatie || 'fără comună'}`;
    if (window.confirm(confirmMessage)) {
      setEditFormData((prev) => {
        const next = { ...prev.groupedParcels };
        next[groupKey] = next[groupKey].filter((_, index) => index !== parcelIndex);
        if (next[groupKey].length === 0) delete next[groupKey];
        return { ...prev, groupedParcels: next };
      });
    }
  };

  // Add new T group for person in edit modal
  const addTGroupToEditPerson = () => {
    const keys = sortTKeys(Object.keys(editFormData.groupedParcels || {}));
    setNewTPrompt({
      value: '', locatie: locationNames[0] || '', keys,
      existing: keys.map((key) => { const { t, locatie } = splitTKey(key); return `T${t} ${locatie}`.trim(); }).join(', '),
    });
  };

  const confirmNewT = () => {
    const tNumber = parseInt(newTPrompt.value, 10);
    if (Number.isNaN(tNumber) || tNumber <= 0) { showToast('Numărul T trebuie să fie pozitiv.', 'error'); return; }
    if (!newTPrompt.locatie) { showToast('Alege comuna acestui T.', 'error'); return; }
    const key = tKeyOf(tNumber, newTPrompt.locatie);
    if (newTPrompt.keys.includes(key)) { showToast(`T${tNumber} există deja în ${newTPrompt.locatie}.`, 'error'); return; }
    addParcelToEditPerson(key);
    setNewTPrompt(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    console.log('🔄 Starting edit submit process...');
    console.log('📊 Edit form data:', editFormData);

    // Show immediate feedback

    // Reset validation errors
    setEditValidationErrors({});

    // Check required fields
    const errors = {};
    if (!editFormData.nume) errors.nume = true;
    if (!editFormData.cnp) errors.cnp = true;
    if (!editFormData.seria) errors.seria = true;
    if (!editFormData.numarulBuletinului) errors.numarulBuletinului = true;
    if (!(editFormData.titluriProprietate || []).some(titlu => titlu.numar && titlu.numar.trim())) errors.titluriProprietate = true;
    if (!editFormData.domiciliat) errors.domiciliat = true;
    if (!editFormData.dataNasterii) errors.dataNasterii = true;
    if (!editFormData.localitateNastere) errors.localitateNastere = true;
    if (!editFormData.judetNastere) errors.judetNastere = true;
    if (!editFormData.firma) errors.firma = true;
    if (!editFormData.dataContract) errors.dataContract = true;
    if (!editFormData.numarContract) errors.numarContract = true;

    // Check CNP format
    if (editFormData.cnp && (editFormData.cnp.length !== 13 || !/^\d{13}$/.test(editFormData.cnp))) {
      errors.cnp = true;
      showToast('CNP-ul trebuie să aibă exact 13 cifre!', 'error');
      setEditValidationErrors(errors);
      return;
    }

    // Check if new CNP already exists (if CNP was changed)
    if (editFormData.cnp !== editFormData.originalCnp) {
      const existingPerson = parcels.find(p => p.cnp === editFormData.cnp);
      if (existingPerson) {
        errors.cnp = true;
        showToast('CNP-ul introdus există deja pentru altă persoană!', 'error');
        setEditValidationErrors(errors);
        return;
      }
    }

    if (Object.keys(errors).length > 0) {
      setEditValidationErrors(errors);
      showToast('Vă rugăm să completați toate câmpurile obligatorii marcate cu roșu!', 'error');
      return;
    }

    try {
      console.log('✅ Validation passed, proceeding with save...');

      // Ensure personParcels exists and is an array
      if (!editFormData.groupedParcels) {
        console.error('❌ personParcels is not an array:', editFormData.personParcels);
        showToast('❌ Eroare la structura datelor! Vă rugăm să reîncărcați pagina.', 'error');
        return;
      }


      // Update all parcels for this person
      // First, remove all existing parcels for this person
      const otherParcels = parcels.filter(p => p.cnp !== editFormData.originalCnp);

      // Then add all parcels from editFormData (updated + new)
      // Get first valid titlu for backward compatibility
      const firstValidTitlu = editFormData.titluriProprietate && editFormData.titluriProprietate.find(titlu => titlu.numar && titlu.numar.trim());

      const editedParcels = Object.values(editFormData.groupedParcels).flat();
      if (editedParcels.some(parcel => !parcel.locatie)) {
        showToast('Alege comuna pentru fiecare T.', 'error');
        return;
      }
      const updatedPersonParcels = editedParcels.map(parcel => ({
        ...parcel,
        suprafata: toNumber(parcel.suprafata),
        // Ensure all personal info is updated
        nume: editFormData.nume,
        cnp: editFormData.cnp,
        seria: editFormData.seria,
        numarulBuletinului: editFormData.numarulBuletinului,
        numarTitluProprietate: firstValidTitlu ? firstValidTitlu.numar : editFormData.numarTitluProprietate || '', // Use first valid titlu for compatibility
        titluriProprietate: editFormData.titluriProprietate, // Multiple titlu de proprietate
        mentiuni: editFormData.mentiuni, // Mentiuni field
        domiciliat: editFormData.domiciliat,
        dataNasterii: editFormData.dataNasterii,
        localitateNastere: editFormData.localitateNastere,
        judetNastere: editFormData.judetNastere,
        firma: editFormData.firma,
        // Each parcel keeps its own comună: T's belong to a comună.
        dataContract: editFormData.dataContract,
        numarContract: editFormData.numarContract,
        contracteAditionale: editFormData.contracteAditionale,
        // Generate proper ID for new parcels
        id: parcel.id && String(parcel.id).startsWith('temp_') ? `${editFormData.cnp}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : parcel.id
      }));

      console.log(`📝 Updated ${updatedPersonParcels.length} parcels for ${editFormData.nume}`);

      // Combine other people's parcels with this person's updated parcels
      const finalParcels = [...otherParcels, ...updatedPersonParcels];
      console.log(`💾 Final dataset: ${finalParcels.length} total parcels`);

      // Update state immediately for instant UI feedback
      setParcels(finalParcels);

      // Save to Firebase
      await saveData(finalParcels);

      // Close modal and show success
      setShowEditModal(false);
      showToast(`✅ Datele pentru ${editFormData.nume} au fost actualizate cu succes!`, 'success');

      console.log('✅ Edit save completed successfully!');

    } catch (error) {
      console.error('❌ Error in handleEditSubmit:', error);
      showToast('❌ Eroare la salvarea datelor! Vă rugăm să încercați din nou.', 'error');
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (editValidationErrors[field]) {
      setEditValidationErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const deletePerson = async (cnp) => {
    const personParcels = parcels.filter(p => p.cnp === cnp);
    if (personParcels.length === 0) {
      showToast('Nu s-au găsit parcele pentru această persoană', 'error');
      return;
    }

    const person = personParcels[0];
    const totalSuprafata = personParcels.reduce((sum, p) => sum + (parseFloat(p.suprafata) || 0), 0);

    const confirmMessage = `Sunteți sigur că doriți să ștergeți TOATE parcelele pentru:\n\n` +
      `Nume: ${person.nume}\n` +
      `CNP: ${person.cnp}\n` +
      `Total parcele: ${personParcels.length}\n` +
      `Suprafață totală: ${totalSuprafata.toFixed(2)} ha\n\n` +
      `Le poți restaura ulterior din Coșul de gunoi, în Setări.`;

    if (window.confirm(confirmMessage)) {
      if (!testStore) {
        // Save to recycle bin BEFORE removing (non-blocking — won't stop the delete if it fails)
        const farmId = localStorage.getItem('currentFarmId');
        const currentUser = await firebaseAuthService.getCurrentUser().catch(() => null);
        const deletedBy = currentUser?.user?.email || currentUser?.user?.name || 'utilizator';
        deletedItemsService.saveToRecycleBin(
          farmId,
          personParcels,
          'parcels',
          `${person.nume} (CNP: ${person.cnp}) — ${personParcels.length} parcele, ${totalSuprafata.toFixed(2)} ha`,
          deletedBy
        );

        // Also keep existing backup
        backupService.backupBeforeDeletion(
          'parcelsData',
          personParcels,
          'person_delete',
          {
            personName: person.nume,
            personCNP: person.cnp,
            deletedBy,
            totalParcels: personParcels.length,
            totalSurface: totalSuprafata
          }
        );
      }

      // Remove from active data immediately (don't wait for backup)
      const newParcels = parcels.filter(p => p.cnp !== cnp);
      setParcels(newParcels);
      await saveData(newParcels);
      showToast(`${personParcels.length} parcele șterse pentru ${person.nume}. Le poți restaura din Coșul de gunoi.`, 'success');
    }
  };

  const getProblemeData = () => {
    try {
      if (!Array.isArray(parcels) || parcels.length === 0) {
        return [];
      }

      // Group parcels by numarTitluProprietate
      const titluGroups = {};

      parcels.forEach(parcel => {
        if (!parcel || !parcel.numarTitluProprietate) return;

        const titlu = parcel.numarTitluProprietate;
        if (!titluGroups[titlu]) {
          titluGroups[titlu] = [];
        }
        titluGroups[titlu].push(parcel);
      });

      // Find probleme (same titlu with different people)
      const probleme = [];

      Object.entries(titluGroups).forEach(([titlu, parcelsWithSameTitlu]) => {
        // Group by person (CNP)
        const peopleWithSameTitlu = {};
        parcelsWithSameTitlu.forEach(parcel => {
          if (!peopleWithSameTitlu[parcel.cnp]) {
            peopleWithSameTitlu[parcel.cnp] = {
              nume: parcel.nume,
              cnp: parcel.cnp,
              parcels: []
            };
          }
          peopleWithSameTitlu[parcel.cnp].parcels.push(parcel);
        });

        // If more than one person has the same titlu, it's a problem
        const people = Object.values(peopleWithSameTitlu);
        if (people.length > 1) {
          probleme.push({
            numarTitluProprietate: titlu,
            people: people,
            totalPeople: people.length,
            totalParcels: parcelsWithSameTitlu.length,
            totalSuprafata: parcelsWithSameTitlu.reduce((sum, p) => sum + (parseFloat(p.suprafata) || 0), 0)
          });
        }
      });

      return probleme.sort((a, b) => b.totalPeople - a.totalPeople);
    } catch (error) {
      console.error('Error in getProblemeData:', error);
      return [];
    }
  };

  const generateContractDeArenda = (person) => {
    try {
      // Get all parcels for this person
      const personParcels = parcels.filter(p => p.cnp === person.cnp);

      if (personParcels.length === 0) {
        showToast('Nu s-au găsit parcele pentru această persoană!', 'error');
        return;
      }

      // Group parcels by T number
      const groupedByT = {};
      personParcels.forEach(parcel => {
        if (!groupedByT[parcel.t]) {
          groupedByT[parcel.t] = [];
        }
        groupedByT[parcel.t].push(parcel);
      });

      // Calculate total surface
      const totalSuprafata = personParcels.reduce((sum, p) => sum + (parseFloat(p.suprafata) || 0), 0);
      const contractCommunes = [...new Set(personParcels.map(p => p.locatie).filter(Boolean))];

      // Get contract date and calculate expiration (10 years later)
      const contractDate = new Date(person.dataContract);
      const expirationDate = new Date(contractDate);
      expirationDate.setFullYear(expirationDate.getFullYear() + 10);

      // Get the company name (use the first parcel's company)
      const companyName = personParcels[0].firma || 'PEPA AGRO SRL';

      // Get company details from Settings (if available)
      const companyDetails = companySettings?.companyDetails?.[companyName] || companySettings?.companies?.find(c => c.name === companyName) || {};
      const administratorName = companyDetails.administratorName || companyDetails.administrator || 'Gheorghiescu Benedict';
      const cui = companyDetails.cui || '4665370';
      const jNumber = companyDetails.jNumber || 'J22/___/20___';
      const companyAddress = companyDetails.address || 'str. Măgurei, jud. Iași';

      // Create contract content matching the user's exact template
      const contractContent = `
<!DOCTYPE html>
<html lang="ro">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Contract de Arendă - ${person.nume}</title>
    <style>
        body {
            font-family: 'Times New Roman', serif;
            font-size: 12px;
            line-height: 1.6;
            margin: 20px;
            color: #000;
            background: white;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            line-height: 1.4;
        }
        .contract-title {
            font-size: 14px;
            font-weight: bold;
            text-decoration: underline;
            margin: 25px 0;
            text-align: center;
        }
        .chapter {
            font-weight: bold;
            margin: 20px 0 15px 0;
            text-decoration: underline;
            font-size: 12px;
        }
        .article {
            margin: 12px 0;
            text-align: justify;
        }
        .indent {
            margin-left: 25px;
        }
        .table-container {
            margin: 20px 0;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
        }
        th, td {
            border: 1px solid #000;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
        }
        th {
            background-color: #f8f8f8;
            font-weight: bold;
        }
        .signature-section {
            margin-top: 60px;
            width: 100%;
        }
        .signature-row {
            display: flex;
            justify-content: space-between;
            margin-top: 40px;
        }
        .signature-box {
            width: 45%;
            text-align: center;
            line-height: 2;
        }
        .underline {
            text-decoration: underline;
        }
        .bold {
            font-weight: bold;
        }
        .center {
            text-align: center;
        }
        .no-print {
            margin: 20px 0;
            text-align: center;
        }
        .print-btn {
            background: #007bff;
            color: white;
            border: none;
            padding: 12px 24px;
            font-size: 14px;
            border-radius: 4px;
            cursor: pointer;
            margin: 0 10px;
        }
        .close-btn {
            background: #6c757d;
            color: white;
            border: none;
            padding: 12px 24px;
            font-size: 14px;
            border-radius: 4px;
            cursor: pointer;
            margin: 0 10px;
        }
        @media print {
            body {
                margin: 15px;
                font-size: 11px;
            }
            .no-print {
                display: none !important;
            }
        }
    </style>
</head>
<body>
    <div class="no-print">
        <button class="print-btn" onclick="window.print()">📄 Printează Contract</button>
        <button class="close-btn" onclick="window.close()">❌ Închide</button>
    </div>

    <div class="header">
        <p><strong>Înregistrat la Consiliul Local al _______________</strong></p>
        <p><strong>Județul _______</strong></p>
        <p><strong>Nr. _______ din _______ 20___</strong></p>
        <p><strong>Secretar, _______________</strong></p>
    </div>

    <div class="contract-title">
        <strong>CONTRACT DE ARENDARE</strong><br>
        <strong>Nr. ${person.numarContract || '_______'}</strong>
    </div>

    <div class="chapter">CAPITOLUL I - Părțile contractante</div>

    <div class="article">
        <strong>1.1.</strong> Părțile contractante sunt:<br><br>
        <div class="indent">
            <strong>a) Arendatorul:</strong> ${companyName}, cu sediul în ${companyAddress}, înfiinţat de prevederea conform art. _____ din O.G. nr. 26/2000, înregistrat în registrul Oficiului Registrului Comerţului prin nr. ${jNumber}, C.U.I. ${cui}, reprezentat prin director ${administratorName}, în baza statutului și a hotărârii Consiliului de Administrație nr. _____ din _____ (denumit în continuare "arendator").<br><br>

            <strong>b) Arendașul:</strong> ${person.nume}, domiciliat în ${person.domiciliat}, născut/ă la data de ${person.dataNasterii} în ${person.localitateNastere}, jud. ${person.judetNastere}, posesor al B.I./C.I. seria ${person.seria} nr. ${person.numarulBuletinului}, eliberat de _______ la data de _______, CNP ${person.cnp}, (denumit în continuare "arendaș").
        </div>
    </div>

    <div class="chapter">CAPITOLUL II - Obiectul contractului</div>

    <div class="article">
        <strong>2.1.</strong> Obiectul contractului îl constituie arendarea terenului agricol în suprafață de <strong>${totalSuprafata.toFixed(2)} ha</strong>, situat în ${contractCommunes.join(', ') || person.locatie}, aparținând arendașului conform tabelului de mai jos:
    </div>

    <div class="table-container">
        <table>
            <thead>
                <tr>
                    <th>Categoria de folosință</th>
                    <th>Tarla</th>
                    <th>Parcela</th>
                    <th>Suprafața</th>
                    <th>Nord</th>
                    <th>Est</th>
                    <th>Sud</th>
                    <th>Vest</th>
                    <th>Numărul Cadastral</th>
                </tr>
            </thead>
            <tbody>
                ${Object.entries(groupedByT).map(([tNumber, parcelsInT]) =>
                  parcelsInT.map(parcel => `
                    <tr>
                        <td>Teren agricol</td>
                        <td>T${tNumber}${contractCommunes.length > 1 ? ` (${parcel.locatie})` : ''}</td>
                        <td>${parcel.parcela || '-'}</td>
                        <td>${parseFloat(parcel.suprafata).toFixed(2)}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>${parcel.cadastralNumber || '-'}</td>
                    </tr>
                  `).join('')
                ).join('')}
            </tbody>
        </table>
    </div>

    <div class="chapter">CAPITOLUL III - Durata contractului</div>

    <div class="article">
        <strong>3.1.</strong> Prezentul contract se încheie pentru o perioadă de 10 ani, începând cu data de ${contractDate.toLocaleDateString('ro-RO')} și se încheie la data de ${expirationDate.toLocaleDateString('ro-RO')}.
    </div>

    <div class="chapter">CAPITOLUL IV - Prețul contractual</div>

    <div class="article">
        <strong>4.1.</strong> Prețul contractual se stabilește prin acordul părților la _______ RON/ha/an, echivalentul în lei al produselor agricole, plătit în primul trimestru al fiecărui an contractual.
    </div>

    <div class="chapter">CAPITOLUL V - Obligațiile părților</div>

    <div class="article">
        <strong>5.1.</strong> Obligațiile arendatorului:<br>
        <div class="indent">
            a) să utilizeze terenul conform destinației și să respecte tehnologiile de cultivare;<br>
            b) să plătească la termen prețul contractual;<br>
            c) să mențină în stare bună terenul arendat;<br>
            d) să restituie terenul la expirarea contractului în starea în care l-a primit.
        </div>
    </div>

    <div class="article">
        <strong>5.2.</strong> Obligațiile arendașului:<br>
        <div class="indent">
            a) să predea terenul în termenul și condițiile stabilite prin contract;<br>
            b) să nu împiedice exercitarea drepturilor arendatorului;<br>
            c) să încaseze la termen prețul contractual.
        </div>
    </div>

    <div class="chapter">CAPITOLUL VI - Încetarea contractului</div>

    <div class="article">
        <strong>6.1.</strong> Prezentul contract încetează:<br>
        <div class="indent">
            a) la expirarea termenului pentru care a fost încheiat;<br>
            b) prin acordul părților;<br>
            c) prin denunțarea unilaterală, cu preaviz de 6 luni.
        </div>
    </div>

    <div class="chapter">CAPITOLUL VII - Notificările între părți</div>

    <div class="article">
        <strong>7.1.</strong> În scopul realizării unei corecte comunicări, părțile vor utiliza adresele stabilite prin prezentul contract.
    </div>

    <div class="chapter">CAPITOLUL VIII - Încetarea contractului</div>

    <div class="article">
        <strong>8.1.</strong> Părțile au convenit ca toate schimbările privind validitatea prezentului contract sau rezultatul din interpretarea, executarea sau încetarea acestuia să fie soluționate pe cale amiabilă prin consultări directe între părți. În caz contrar, litigiile vor fi soluționate de către instanțele judecătorești competente.
    </div>

    <div class="chapter">CAPITOLUL IX - Dreptul contractual</div>

    <div class="article">
        <strong>9.1.</strong> Contractul de arendare se încheie de drept pentru aceeași durată dacă nu este dat preaviz de încetare în termen de 6 luni înainte de expirarea termenului contractual, dar nu mai mult de 10 ani.
    </div>

    <div class="chapter">CAPITOLUL X - Clauze finale</div>

    <div class="article">
        <strong>10.1</strong> Contractul de arendare constituie titlu executoriu pentru plata arendei, la cererea oricărei părți.<br>
        <strong>10.2.</strong> Prezentul contract reprezintă voinţa părţilor şi înlocuieşte orice alte înţelegeri verbale dintre aceste anterioare acestuia.<br>
        <strong>10.3</strong> Prezentul contract nu include în 3 (trei) exemplare, din care pentru fiecare parte unul și unul pentru Consiliul Local al Comunei.
    </div>

    <div class="signature-section">
        <div class="signature-row">
            <div class="signature-box">
                <strong>ARENDATOR</strong><br>
                ${companyName}<br><br>
                Director: ${administratorName}
            </div>
            <div class="signature-box">
                <strong>ARENDAȘ</strong><br><br>
                ${person.nume}
            </div>
        </div>
    </div>

</body>
</html>
      `;

      // Open the contract in a new window
      const contractWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
      if (!contractWindow) {
        showToast('Fereastra contractului a fost blocată. Permite ferestrele noi și încearcă din nou.', 'error');
        return;
      }
      contractWindow.document.write(contractContent);
      contractWindow.document.close();

      showToast(`✅ Contract generat pentru ${person.nume}!`, 'success');

    } catch (error) {
      console.error('Error generating contract:', error);
      showToast('❌ Eroare la generarea contractului!', 'error');
    }
  };

  // Handle multiple titlu de proprietate in edit modal
  const addEditTitluProprietate = () => {
    setEditFormData(prev => ({
      ...prev,
      titluriProprietate: [...(prev.titluriProprietate || []), { numar: '', data: '' }]
    }));
  };

  const removeEditTitluProprietate = (index) => {
    if (editFormData.titluriProprietate && editFormData.titluriProprietate.length > 1) {
      setEditFormData(prev => ({
        ...prev,
        titluriProprietate: prev.titluriProprietate.filter((_, i) => i !== index)
      }));
    }
  };

  const updateEditTitluProprietate = (index, field, value) => {
    setEditFormData(prev => ({
      ...prev,
      titluriProprietate: (prev.titluriProprietate || []).map((titlu, i) =>
        i === index ? { ...titlu, [field]: value } : titlu
      )
    }));
  };

  // ── Screen ───────────────────────────────────────────────────────────────────

  const groups = view === 'view' ? getGroupedParcels() : [];
  const people = view === 'people' ? getPeopleData() : [];
  const probleme = getProblemeData();
  const anyFilter = Boolean(filters.t || filters.location || filters.company || filters.name || filters.titluProprietate || filters.hasCadastral);
  const clearFilters = () => setFilters({ t: '', location: '', name: '', titluProprietate: '', company: '', sortBy: 'alfabetic', hasCadastral: false });

  const allLocations = [...new Set(parcels.map((p) => p.locatie).filter(Boolean))].sort();
  const allCompanies = [...new Set(parcels.map((p) => p.firma).filter(Boolean))];
  const allT = [...new Set(parcels.filter((p) => !filters.location || p.locatie === filters.location).map((p) => Number(p.t)).filter(Boolean))].sort((a, b) => a - b);

  const summary = view === 'view'
    ? [
      { label: anyFilter ? 'parcele (filtrat)' : 'parcele', value: groups.reduce((sum, g) => sum + g.parcele.length, 0) },
      { label: 'hectare', value: formatHa(groups.reduce((sum, g) => sum + g.totalSuprafata, 0)) },
    ]
    : [
      { label: anyFilter ? 'persoane (filtrat)' : 'persoane', value: people.length },
      { label: 'hectare', value: formatHa(people.reduce((sum, p) => sum + (p.totalSuprafata || 0), 0)) },
      { label: 'expiră în 2 ani', value: people.filter((p) => expiresWithinTwoYears(p.dataContract)).length },
    ];

  const addTotalHa = tGroups.reduce((sum, g) => sum + g.parcels.reduce((s, row) => s + toNumber(row.suprafata), 0), 0);
  const STEPS = ['Persoană', 'Titluri', 'Contract', 'Parcele'];
  const editHa = Object.values(editFormData.groupedParcels || {}).flat().reduce((sum, p) => sum + toNumber(p.suprafata), 0);

  const setForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) setValidationErrors((prev) => ({ ...prev, [field]: false }));
  };

  const updateAddCnp = (value) => {
    setForm('cnp', value);
    if (value.length === 13) {
      const birthData = extractBirthDataFromCNP(value);
      if (birthData.isValid) {
        setFormData((prev) => ({ ...prev, cnp: value, dataNasterii: birthData.birthDate, judetNastere: birthData.birthCounty }));
      }
    }
  };

  return (
    <div className="pm">
      {!isOnline && <div className="pm-banner" role="status">Ești offline. Modificările se salvează pe acest dispozitiv și se sincronizează când revine conexiunea.</div>}

      <header className="pm-head">
        <h1>Parcele</h1>
        <div className="pm-head-actions">
          {isSaving && <span className="pm-saving" role="status">Se salvează…</span>}
          {view !== 'probleme' && <button type="button" className="pm-btn-plain" onClick={exportToExcel}>Exportă</button>}
          <button type="button" className="pm-btn-primary pm-only-wide" onClick={() => setShowAdd(true)}><Icon d={ICON.plus} size={18} />Adaugă parcele</button>
        </div>
      </header>

      <div className="pm-segments" role="tablist">
        {[['view', 'Parcele'], ['people', 'Persoane'], ['probleme', `Probleme${probleme.length ? ` · ${probleme.length}` : ''}`]].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} className={view === id ? 'is-on' : ''} onClick={() => setView(id)}>{label}</button>
        ))}
      </div>

      {view !== 'probleme' && (
        <>
          <div className="pm-search">
            <Icon d={ICON.search} size={18} />
            <input type="search" aria-label="Caută după nume sau CNP" placeholder="Caută după nume sau CNP" value={filters.name}
              onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))} />
          </div>
          <div className="pm-chips">
            {view === 'people' && (
              <select aria-label="Sortează după" className="pm-chip" value={filters.sortBy} onChange={(e) => setFilters((prev) => ({ ...prev, sortBy: e.target.value }))}>
                <option value="alfabetic">Alfabetic</option>
                <option value="suprafata_mare_mic">Suprafață, mare → mic</option>
                <option value="suprafata_mic_mare">Suprafață, mic → mare</option>
                <option value="dataContract">Data contractului</option>
                <option value="expirare">Data expirării</option>
              </select>
            )}
            <select aria-label="Comună" className={`pm-chip ${filters.location ? 'is-on' : ''}`} value={filters.location}
              onChange={(e) => setFilters((prev) => ({ ...prev, location: e.target.value, t: '' }))}>
              <option value="">Comună</option>
              {allLocations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
            </select>
            {view === 'view' && (
              <select aria-label="T" className={`pm-chip ${filters.t ? 'is-on' : ''}`} value={filters.t} onChange={(e) => setFilters((prev) => ({ ...prev, t: e.target.value }))}>
                <option value="">T</option>
                {allT.map((t) => <option key={t} value={t}>T{t}</option>)}
              </select>
            )}
            <select aria-label="Firmă" className={`pm-chip ${filters.company ? 'is-on' : ''}`} value={filters.company} onChange={(e) => setFilters((prev) => ({ ...prev, company: e.target.value }))}>
              <option value="">Firmă</option>
              {allCompanies.map((company) => <option key={company} value={company}>{company}</option>)}
            </select>
            <button type="button" aria-pressed={filters.hasCadastral} className={`pm-chip ${filters.hasCadastral ? 'is-on' : ''}`}
              onClick={() => setFilters((prev) => ({ ...prev, hasCadastral: !prev.hasCadastral }))}>Cu nr. cadastral</button>
            <input aria-label="Caută după titlu de proprietate" placeholder="Titlu proprietate" className={`pm-chip pm-chip-input ${filters.titluProprietate ? 'is-on' : ''}`}
              value={filters.titluProprietate} onChange={(e) => setFilters((prev) => ({ ...prev, titluProprietate: e.target.value }))} />
            {anyFilter && <button type="button" className="pm-chip pm-chip-clear" onClick={clearFilters}>Șterge filtrele</button>}
          </div>
          <div className="pm-summary">
            {summary.map((item) => <div key={item.label}><strong>{item.value}</strong><span>{item.label}</span></div>)}
          </div>
        </>
      )}

      {loading && parcels.length === 0 && <p className="pm-empty">Se încarcă parcelele…</p>}

      {view === 'view' && (
        <div className="pm-list">
          {groups.map((group) => (
            <button key={`${group.t}-${group.locatie}-${group.nume}-${group.cnp}-${group.firma}`} type="button" className="pm-row" onClick={() => showParcelsDetails(group)}>
              <span className="pm-t">T{group.t}</span>
              <span className="pm-row-main">
                <strong>{group.nume}</strong>
                <span>{group.parcele.length} {group.parcele.length === 1 ? 'parcelă' : 'parcele'} · {group.locatie}</span>
              </span>
              <span className="pm-row-end">{formatHa(group.totalSuprafata)} ha</span>
            </button>
          ))}
          {!loading && groups.length === 0 && <p className="pm-empty">{anyFilter ? 'Nicio parcelă nu corespunde filtrelor.' : 'Nu există parcele încă. Apasă + pentru a adăuga.'}</p>}
        </div>
      )}

      {view === 'people' && (
        <div className="pm-list">
          {people.map((person) => {
            const places = [...new Set((person.parcels || []).map((p) => p.locatie).filter(Boolean))];
            const tNumbers = sortTKeys([...new Set((person.parcels || []).map((p) => tKeyOf(Number(p.t), p.locatie)))])
              .map((key) => { const { t, locatie } = splitTKey(key); return places.length > 1 ? `T${t} (${locatie || 'fără comună'})` : `T${t}`; }).join(', ');
            const expiring = expiresWithinTwoYears(person.dataContract);
            return (
              <button key={person.cnp} type="button" className="pm-row" onClick={() => showPersonDetails(person.cnp)}>
                <span className="pm-row-main">
                  <strong>{person.nume}</strong>
                  <span>{tNumbers || '—'}{places.length === 1 ? ` · ${places[0]}` : ''} · {person.totalParcele} {person.totalParcele === 1 ? 'parcelă' : 'parcele'}</span>
                  <span className={expiring ? 'pm-warn' : ''}>
                    {expiring ? 'Contract expiră ' : 'Contract până '}{contractExpiry(person.dataContract) ? contractExpiry(person.dataContract).toLocaleDateString('ro-RO') : '—'}
                  </span>
                </span>
                <span className="pm-row-end">{formatHa(person.totalSuprafata)} ha</span>
              </button>
            );
          })}
          {!loading && people.length === 0 && <p className="pm-empty">{anyFilter ? 'Nicio persoană nu corespunde filtrelor.' : 'Nu există persoane încă.'}</p>}
        </div>
      )}

      {view === 'probleme' && (
        <div className="pm-list">
          <p className="pm-note">Aici apare un titlu de proprietate folosit de persoane diferite. Poate fi o suprapunere sau o eroare de introducere, de verificat manual.</p>
          {probleme.length === 0 && <p className="pm-empty">Nicio problemă. Toate titlurile sunt unice pentru fiecare persoană.</p>}
          {probleme.map((problema) => (
            <section key={problema.numarTitluProprietate} className="pm-card">
              <h2>Titlu {problema.numarTitluProprietate}</h2>
              <p className="pm-muted">{problema.totalPeople} persoane · {problema.totalParcels} parcele · {problema.totalSuprafata.toFixed(2)} ha</p>
              {problema.people.map((person) => (
                <button key={person.cnp} type="button" className="pm-row pm-row-flat" onClick={() => showPersonDetails(person.cnp)}>
                  <span className="pm-row-main">
                    <strong>{person.nume}</strong>
                    <span>{sortTKeys([...new Set(person.parcels.map((p) => tKeyOf(Number(p.t), p.locatie)))]).map((key) => { const { t, locatie } = splitTKey(key); return `T${t} ${locatie}`.trim(); }).join(', ')} · CNP {person.cnp}</span>
                  </span>
                  <span className="pm-row-end">{formatHa(person.parcels.reduce((sum, p) => sum + toNumber(p.suprafata), 0))} ha</span>
                </button>
              ))}
            </section>
          ))}
        </div>
      )}

      <button type="button" className="pm-fab" aria-label="Adaugă parcele" onClick={() => setShowAdd(true)}><Icon d={ICON.plus} size={26} /></button>

      {/* ── Adding, in four steps ── */}
      {showAdd && (
        <Panel title="Parcele noi" subtitle={`Pasul ${addStep + 1} din 4 · ${STEPS[addStep]}`} onClose={() => setShowAdd(false)} onSubmit={handleSubmit}
          footer={(
            <>
              <button type="button" className="pm-btn-soft" onClick={() => (addStep === 0 ? setShowAdd(false) : setAddStep((step) => step - 1))}>{addStep === 0 ? 'Închide' : 'Înapoi'}</button>
              {addStep < 3
                ? <button type="button" className="pm-btn-primary" onClick={nextAddStep}>Continuă la {STEPS[addStep + 1].toLowerCase()}</button>
                : <button type="submit" className="pm-btn-accent" disabled={isSaving}>Salvează parcelele</button>}
            </>
          )}>
          <div className="pm-steps" aria-hidden="true">
            {STEPS.map((label, index) => (
              <div key={label} className={index <= addStep ? 'is-done' : ''}><i /><span>{label}</span></div>
            ))}
          </div>

          {addStep === 0 && (
            <>
              <Field label="Nume" error={validationErrors.nume}>
                <input type="text" value={formData.nume} autoComplete="off" onChange={(e) => updateOwnerName(e.target.value)} />
              </Field>
              <Field label="CNP" error={validationErrors.cnp} hint="13 cifre. Data și județul nașterii se completează singure.">
                <input type="text" inputMode="numeric" maxLength={13} autoComplete="off" value={formData.cnp} onChange={(e) => updateAddCnp(e.target.value.trim())} />
              </Field>
              <Field label="Domiciliat/ă în" error={validationErrors.domiciliat}>
                <input type="text" placeholder="ex: str. Măgurei, jud. Iași" value={formData.domiciliat} onChange={(e) => setForm('domiciliat', e.target.value)} />
              </Field>
              <div className="pm-two">
                <Field label="Născut/ă la data de" error={validationErrors.dataNasterii}>
                  <input type="text" placeholder="ZZ.LL.AAAA" value={formData.dataNasterii} onChange={(e) => setForm('dataNasterii', e.target.value)} />
                </Field>
                <Field label="Județul nașterii" error={validationErrors.judetNastere}>
                  <input type="text" value={formData.judetNastere} onChange={(e) => setForm('judetNastere', e.target.value)} />
                </Field>
              </div>
              <Field label="Născut/ă în localitatea" error={validationErrors.localitateNastere}>
                <input type="text" value={formData.localitateNastere} onChange={(e) => setForm('localitateNastere', e.target.value)} />
              </Field>
              <div className="pm-two">
                <Field label="Seria buletinului" error={validationErrors.seria} hint="2 litere">
                  <input type="text" maxLength={2} autoCapitalize="characters" value={formData.seria} onChange={(e) => setForm('seria', e.target.value.toUpperCase())} />
                </Field>
                <Field label="Numărul buletinului" error={validationErrors.numarulBuletinului} hint="6 cifre">
                  <input type="text" inputMode="numeric" maxLength={6} value={formData.numarulBuletinului} onChange={(e) => setForm('numarulBuletinului', e.target.value.trim())} />
                </Field>
              </div>
            </>
          )}

          {addStep === 1 && (
            <>
              {validationErrors.titluriProprietate && <p className="pm-error">Adaugă cel puțin un titlu de proprietate.</p>}
              {formData.titluriProprietate.map((titlu, index) => (
                <section key={index} className="pm-card">
                  <div className="pm-card-head">
                    <h3>Titlu {index + 1}</h3>
                    {formData.titluriProprietate.length > 1 && <button type="button" className="pm-link-danger" onClick={() => removeTitluProprietate(index)}>Șterge</button>}
                  </div>
                  <div className="pm-two">
                    <Field label="Număr titlu"><input type="text" value={titlu.numar} onChange={(e) => updateTitluProprietate(index, 'numar', e.target.value)} /></Field>
                    <Field label="Data (opțional)"><input type="date" value={titlu.data} onChange={(e) => updateTitluProprietate(index, 'data', e.target.value)} /></Field>
                  </div>
                </section>
              ))}
              <button type="button" className="pm-btn-dashed" onClick={addTitluProprietate}>+ Adaugă titlu de proprietate</button>
              <Field label="Mențiuni (opțional)">
                <textarea rows={3} value={formData.mentiuni} onChange={(e) => setForm('mentiuni', e.target.value)} />
              </Field>
            </>
          )}

          {addStep === 2 && (
            <>
              {(companyNames.length === 0 || locationNames.length === 0) && !loading && (
                <p className="pm-note">Lipsesc firme sau comune. Le poți adăuga din Mai mult → Setări → Companii și locații.</p>
              )}
              <Field label="Firma" error={validationErrors.firma}>
                <select value={formData.firma} disabled={loading} onChange={(e) => setForm('firma', e.target.value)}>
                  <option value="">Selectează firma</option>
                  {companyNames.map((company) => <option key={company} value={company}>{company}</option>)}
                </select>
              </Field>
              <Field label="Comuna contractului" error={validationErrors.locatie} hint="La pasul următor, fiecare T poate avea altă comună.">
                <select value={formData.locatie} disabled={loading} onChange={(e) => setForm('locatie', e.target.value)}>
                  <option value="">Selectează comuna</option>
                  {locationNames.map((location) => <option key={location} value={location}>{location}</option>)}
                </select>
              </Field>
              <div className="pm-two">
                <Field label="Numărul contractului" error={validationErrors.numarContract}>
                  <input type="text" value={formData.numarContract} onChange={(e) => setForm('numarContract', e.target.value)} />
                </Field>
                <Field label="Data înregistrării" error={validationErrors.dataContract}>
                  <input type="date" value={formData.dataContract} onChange={(e) => setForm('dataContract', e.target.value)} />
                </Field>
              </div>
              {formData.contracteAditionale.map((contract, index) => (
                <section key={index} className={`pm-card ${validationErrors[`contract_${index}`] ? 'has-error' : ''}`}>
                  <div className="pm-card-head">
                    <h3>Contract adițional {index + 1}</h3>
                    <button type="button" className="pm-link-danger" onClick={() => removeAdditionalContract(index)}>Șterge</button>
                  </div>
                  <div className="pm-two">
                    <Field label="Data contract"><input type="date" value={contract.dataContract} onChange={(e) => updateAdditionalContract(index, 'dataContract', e.target.value)} /></Field>
                    <Field label="Număr contract"><input type="text" value={contract.numarContract} onChange={(e) => updateAdditionalContract(index, 'numarContract', e.target.value)} /></Field>
                  </div>
                  <div className="pm-two">
                    <Field label="Firma">
                      <select value={contract.firma} onChange={(e) => updateAdditionalContract(index, 'firma', e.target.value)}>
                        <option value="">Selectează</option>
                        {companyNames.map((company) => <option key={company} value={company}>{company}</option>)}
                      </select>
                    </Field>
                    <Field label="Locația">
                      <select value={contract.locatie} onChange={(e) => updateAdditionalContract(index, 'locatie', e.target.value)}>
                        <option value="">Selectează</option>
                        {locationNames.map((location) => <option key={location} value={location}>{location}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Descriere"><textarea rows={2} value={contract.descriere} onChange={(e) => updateAdditionalContract(index, 'descriere', e.target.value)} /></Field>
                </section>
              ))}
              <button type="button" className="pm-btn-dashed" onClick={addAdditionalContract}>+ Contract adițional</button>
            </>
          )}

          {addStep === 3 && (
            <>
              <div className="pm-add-total"><strong>{addTotalHa.toFixed(2)} ha</strong><span>{tGroups.reduce((sum, g) => sum + g.parcels.length, 0)} parcele pentru {formData.nume}</span></div>
              {tGroups.map((group) => (
                <section key={group.key} className="pm-card">
                  <div className="pm-card-head">
                    <h3>{group.t ? `T${group.t}` : 'T nou'}{group.locatie ? `, ${group.locatie}` : ''}</h3>
                    {tGroups.length > 1 && <button type="button" className="pm-link-danger" onClick={() => removeTGroup(group.key)}>Șterge T</button>}
                  </div>
                  <div className="pm-two">
                    <Field label="Număr T" error={validationErrors[`t_${group.key}`]}>
                      <input type="text" inputMode="numeric" value={group.t} onChange={(e) => updateTNumber(group.key, e.target.value.replace(/\D/g, ''))} />
                    </Field>
                    <Field label="Comuna acestui T" error={validationErrors[`l_${group.key}`]}>
                      <select value={group.locatie} onChange={(e) => updateTLocatie(group.key, e.target.value)}>
                        <option value="">Selectează</option>
                        {locationNames.map((location) => <option key={location} value={location}>{location}</option>)}
                      </select>
                    </Field>
                  </div>
                  {group.parcels.map((row, index) => (
                    <div key={row.key} className="pm-parcel">
                      <div className="pm-card-head">
                        <h3>Parcela {index + 1}</h3>
                        {group.parcels.length > 1 && <button type="button" className="pm-link-danger" onClick={() => removeParcelInput(group.key, row.key)}>Șterge</button>}
                      </div>
                      <div className="pm-two">
                        <Field label="Parcela"><input type="text" placeholder="A1, B2" value={row.parcela} onChange={(e) => updateParcelInput(group.key, row.key, { parcela: e.target.value })} /></Field>
                        <Field label="Suprafață (ha)" error={validationErrors[`s_${row.key}`]}>
                          <input type="text" inputMode="decimal" placeholder="0.60" value={row.suprafata}
                            onChange={(e) => { const value = e.target.value.replace(',', '.'); if (value === '' || /^\d*\.?\d*$/.test(value)) updateParcelInput(group.key, row.key, { suprafata: value }); }}
                            onBlur={() => tryAutocomplete(group, row)} />
                        </Field>
                      </div>
                      <div className="pm-two">
                        <Field label="Nr. cadastral" hint={row.mark === 'exact' ? 'Găsit în baza de date' : row.mark === 'check' ? 'Verifică suprafața' : 'Se caută automat'}>
                          <input type="text" className={row.mark ? `pm-mark-${row.mark}` : ''} value={row.cadastralNumber} onChange={(e) => updateParcelInput(group.key, row.key, { cadastralNumber: e.target.value, mark: '' })} />
                        </Field>
                        <Field label="Categorie">
                          <select value={row.categorie} onChange={(e) => updateParcelInput(group.key, row.key, { categorie: e.target.value })}>
                            <option value="arabil">Arabil</option>
                            <option value="fanete">Fânețe</option>
                            <option value="pasune">Pășune</option>
                          </select>
                        </Field>
                      </div>
                      <details className="pm-more">
                        <summary>Hotare (opțional)</summary>
                        <div className="pm-two">
                          {[['nord', 'Nord'], ['est', 'Est'], ['sud', 'Sud'], ['vest', 'Vest']].map(([key, label]) => (
                            <Field key={key} label={label}><input type="text" value={row[key]} onChange={(e) => updateParcelInput(group.key, row.key, { [key]: e.target.value })} /></Field>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))}
                  <button type="button" className="pm-btn-dashed" onClick={() => addParcelInput(group.key)}>+ Parcelă în {group.t ? `T${group.t}` : 'acest T'}</button>
                </section>
              ))}
              <button type="button" className="pm-btn-soft pm-wide" onClick={addTParcelGroup}>+ Adaugă alt T</button>
            </>
          )}
        </Panel>
      )}

      {/* ── A person, or a T group ── */}
      {showDetailsModal && detailsData && (
        <Panel title={detailsData.type === 'person' ? detailsData.person.nume : `T${detailsData.group.t}, ${detailsData.group.locatie} · ${detailsData.group.nume}`}
          subtitle={detailsData.type === 'person' ? `CNP ${detailsData.person.cnp}` : `${detailsData.parcels.length} parcele · ${formatHa(detailsData.totalArea)} ha`}
          onClose={() => setShowDetailsModal(false)}
          footer={detailsData.type === 'group' ? (
            <>
              <button type="button" className="pm-btn-soft pm-danger" onClick={() => { const { group } = detailsData; setShowDetailsModal(false); deleteGroup(group); }}>Șterge din T{detailsData.group.t}</button>
              <button type="button" className="pm-btn-primary" onClick={() => showPersonDetails(detailsData.group.cnp)}>Vezi persoana</button>
            </>
          ) : null}>
          {detailsData.type === 'person' && (
            <>
              <div className="pm-actions">
                <button type="button" className="is-primary" onClick={() => { const { cnp } = detailsData.person; setShowDetailsModal(false); editPerson(cnp); }}><Icon d={ICON.edit} />Editează</button>
                <button type="button" onClick={() => generateContractDeArenda(detailsData.person)}><Icon d={ICON.doc} />Contract</button>
                <button type="button" className="is-danger" onClick={() => { const { cnp } = detailsData.person; setShowDetailsModal(false); deletePerson(cnp); }}><Icon d={ICON.trash} />Șterge</button>
              </div>
              <section className="pm-card">
                <h3>Date personale</h3>
                <dl className="pm-rows">
                  <div><dt>Născut/ă</dt><dd>{detailsData.person.dataNasterii || '—'}, {detailsData.person.localitateNastere || '—'}, {detailsData.person.judetNastere || '—'}</dd></div>
                  <div><dt>Domiciliu</dt><dd>{detailsData.person.domiciliat || '—'}</dd></div>
                  <div><dt>Buletin</dt><dd>{detailsData.person.seria || '—'} {detailsData.person.numarulBuletinului || ''}</dd></div>
                  <div><dt>Titluri</dt><dd>{(detailsData.person.titluriProprietate?.filter((titlu) => titlu.numar).map((titlu) => titlu.numar).join(', ')) || detailsData.person.numarTitluProprietate || '—'}</dd></div>
                  {detailsData.person.mentiuni && <div><dt>Mențiuni</dt><dd>{detailsData.person.mentiuni}</dd></div>}
                </dl>
              </section>
              <section className="pm-card">
                <h3>Contract</h3>
                <dl className="pm-rows">
                  <div><dt>Firma</dt><dd>{detailsData.person.firma || '—'}</dd></div>
                  <div><dt>Comune</dt><dd>{[...new Set(Object.values(detailsData.groupedByT || {}).flat().map((p) => p.locatie).filter(Boolean))].join(', ') || '—'}</dd></div>
                  <div><dt>Număr</dt><dd>{detailsData.person.numarContract || '—'}</dd></div>
                  <div><dt>Data</dt><dd>{formatDate(detailsData.person.dataContract)}</dd></div>
                  <div><dt>Expiră</dt><dd className={expiresWithinTwoYears(detailsData.person.dataContract) ? 'pm-warn' : ''}>{contractExpiry(detailsData.person.dataContract)?.toLocaleDateString('ro-RO') || '—'}</dd></div>
                </dl>
                {(detailsData.person.contracteAditionale || []).map((contract, index) => (
                  <p key={index} className="pm-muted">Adițional: {contract.numarContract || contract.numar || '—'} · {formatDate(contract.dataContract || contract.data)}{contract.descriere ? ` · ${contract.descriere}` : ''}</p>
                ))}
              </section>
              <div className="pm-summary">
                <div><strong>{Object.keys(detailsData.groupedByT || {}).length}</strong><span>T-uri</span></div>
                <div><strong>{Object.values(detailsData.groupedByT || {}).flat().length}</strong><span>parcele</span></div>
                <div><strong>{formatHa(Object.values(detailsData.groupedByT || {}).flat().reduce((sum, p) => sum + toNumber(p?.suprafata), 0))}</strong><span>hectare</span></div>
              </div>
              {sortTKeys(Object.keys(detailsData.groupedByT || {})).map((key) => {
                const { t, locatie } = splitTKey(key);
                return (
                  <section key={key} className="pm-card">
                    <h3>T{t}{locatie ? `, ${locatie}` : ''} · {formatHa(detailsData.groupedByT[key].reduce((sum, p) => sum + toNumber(p?.suprafata), 0))} ha</h3>
                    {detailsData.groupedByT[key].map((parcel, index) => <ParcelLine key={parcel.id || index} parcel={parcel} />)}
                  </section>
                );
              })}
            </>
          )}
          {detailsData.type === 'group' && (
            <section className="pm-card">
              {detailsData.parcels.map((parcel, index) => <ParcelLine key={parcel.id || index} parcel={parcel} />)}
            </section>
          )}
        </Panel>
      )}

      {/* ── Editing a person ── */}
      {showEditModal && (
        <Panel title="Editează persoana" subtitle={`Se aplică tuturor parcelelor · ${editHa.toFixed(2)} ha`} onClose={() => setShowEditModal(false)} onSubmit={handleEditSubmit}
          footer={(
            <>
              <button type="button" className="pm-btn-soft" onClick={() => setShowEditModal(false)}>Anulează</button>
              <button type="submit" className="pm-btn-primary" disabled={isSaving}>Salvează modificările</button>
            </>
          )}>
          <section className="pm-card">
            <h3>Date personale</h3>
            <Field label="Nume" error={editValidationErrors.nume}><input type="text" value={editFormData.nume || ''} onChange={(e) => handleEditInputChange('nume', e.target.value)} /></Field>
            <Field label="CNP" error={editValidationErrors.cnp} hint="13 cifre. Completează data și județul nașterii.">
              <input type="text" inputMode="numeric" maxLength={13} value={editFormData.cnp || ''} onChange={(e) => handleEditCNPChange(e.target.value.trim())} />
            </Field>
            <Field label="Domiciliat/ă în" error={editValidationErrors.domiciliat}><input type="text" value={editFormData.domiciliat || ''} onChange={(e) => handleEditInputChange('domiciliat', e.target.value)} /></Field>
            <div className="pm-two">
              <Field label="Născut/ă la data de" error={editValidationErrors.dataNasterii}><input type="text" placeholder="ZZ.LL.AAAA" value={editFormData.dataNasterii || ''} onChange={(e) => handleEditInputChange('dataNasterii', e.target.value)} /></Field>
              <Field label="Județul nașterii" error={editValidationErrors.judetNastere}><input type="text" value={editFormData.judetNastere || ''} onChange={(e) => handleEditInputChange('judetNastere', e.target.value)} /></Field>
            </div>
            <Field label="Născut/ă în localitatea" error={editValidationErrors.localitateNastere}><input type="text" value={editFormData.localitateNastere || ''} onChange={(e) => handleEditInputChange('localitateNastere', e.target.value)} /></Field>
            <div className="pm-two">
              <Field label="Seria buletinului" error={editValidationErrors.seria}><input type="text" value={editFormData.seria || ''} onChange={(e) => handleEditInputChange('seria', e.target.value)} /></Field>
              <Field label="Numărul buletinului" error={editValidationErrors.numarulBuletinului}><input type="text" inputMode="numeric" value={editFormData.numarulBuletinului || ''} onChange={(e) => handleEditInputChange('numarulBuletinului', e.target.value)} /></Field>
            </div>
          </section>

          <section className={`pm-card ${editValidationErrors.titluriProprietate ? 'has-error' : ''}`}>
            <h3>Titluri de proprietate</h3>
            {(editFormData.titluriProprietate || []).map((titlu, index) => (
              <div key={index} className="pm-parcel">
                <div className="pm-card-head">
                  <h3>Titlu {index + 1}</h3>
                  {editFormData.titluriProprietate.length > 1 && <button type="button" className="pm-link-danger" onClick={() => removeEditTitluProprietate(index)}>Șterge</button>}
                </div>
                <div className="pm-two">
                  <Field label="Număr titlu"><input type="text" value={titlu.numar || ''} onChange={(e) => updateEditTitluProprietate(index, 'numar', e.target.value)} /></Field>
                  <Field label="Data (opțional)"><input type="date" value={titlu.data || ''} onChange={(e) => updateEditTitluProprietate(index, 'data', e.target.value)} /></Field>
                </div>
              </div>
            ))}
            <button type="button" className="pm-btn-dashed" onClick={addEditTitluProprietate}>+ Adaugă titlu de proprietate</button>
            <Field label="Mențiuni (opțional)"><textarea rows={3} value={editFormData.mentiuni || ''} onChange={(e) => handleEditInputChange('mentiuni', e.target.value)} /></Field>
          </section>

          <section className="pm-card">
            <h3>Contract</h3>
            <Field label="Firma" error={editValidationErrors.firma}>
              <select value={editFormData.firma || ''} onChange={(e) => handleEditInputChange('firma', e.target.value)}>
                {!companyNames.includes(editFormData.firma) && <option value={editFormData.firma || ''}>{editFormData.firma || 'Selectează'}</option>}
                {companyNames.map((company) => <option key={company} value={company}>{company}</option>)}
              </select>
            </Field>
            <div className="pm-two">
              <Field label="Număr contract" error={editValidationErrors.numarContract}><input type="text" value={editFormData.numarContract || ''} onChange={(e) => handleEditInputChange('numarContract', e.target.value)} /></Field>
              <Field label="Data contract" error={editValidationErrors.dataContract}><input type="date" value={editFormData.dataContract || ''} onChange={(e) => handleEditInputChange('dataContract', e.target.value)} /></Field>
            </div>
          </section>

          {sortTKeys(Object.keys(editFormData.groupedParcels || {})).map((tNumber) => {
            const tParcels = editFormData.groupedParcels[tNumber] || [];
            const { t: tLabel, locatie: tPlace } = splitTKey(tNumber);
            return (
              <section key={tNumber} className="pm-card">
                <h3>T{tLabel} · {formatHa(tParcels.reduce((sum, p) => sum + toNumber(p.suprafata), 0))} ha</h3>
                <Field label={`Comuna pentru T${tLabel}`} error={!tPlace}>
                  <select value={tPlace} onChange={(e) => changeEditGroupLocatie(tNumber, e.target.value)}>
                    {!locationNames.includes(tPlace) && <option value={tPlace}>{tPlace || 'Selectează'}</option>}
                    {locationNames.map((location) => <option key={location} value={location}>{location}</option>)}
                  </select>
                </Field>
                {tParcels.map((parcel, index) => (
                  <div key={parcel.id || index} className="pm-parcel">
                    <div className="pm-card-head">
                      <h3>Parcela {parcel.parcela || index + 1}</h3>
                      <button type="button" className="pm-link-danger" onClick={() => deleteParcelFromEditPerson(tNumber, index)}>Șterge</button>
                    </div>
                    <div className="pm-two">
                      <Field label="Parcela"><input type="text" value={parcel.parcela || ''} onChange={(e) => handleParcelUpdate(tNumber, index, 'parcela', e.target.value)} /></Field>
                      <Field label="Suprafață (ha)">
                        <input type="text" inputMode="decimal" value={parcel.suprafata ?? ''} onFocus={(e) => e.target.select()}
                          onChange={(e) => { const value = e.target.value.replace(',', '.'); if (value === '' || /^\d*\.?\d*$/.test(value)) handleParcelUpdate(tNumber, index, 'suprafata', value); }} />
                      </Field>
                    </div>
                    <div className="pm-two">
                      <Field label="Nr. cadastral"><input type="text" value={parcel.cadastralNumber || ''} onChange={(e) => handleParcelUpdate(tNumber, index, 'cadastralNumber', e.target.value)} /></Field>
                      <Field label="Categorie">
                        <select value={parcel.categorie || 'arabil'} onChange={(e) => handleParcelUpdate(tNumber, index, 'categorie', e.target.value)}>
                          <option value="arabil">Arabil</option>
                          <option value="fanete">Fânețe</option>
                          <option value="pasune">Pășune</option>
                        </select>
                      </Field>
                    </div>
                    <details className="pm-more">
                      <summary>Hotare</summary>
                      <div className="pm-two">
                        {[['nord', 'Nord'], ['est', 'Est'], ['sud', 'Sud'], ['vest', 'Vest']].map(([key, label]) => (
                          <Field key={key} label={label}><input type="text" value={parcel[key] || ''} onChange={(e) => handleParcelUpdate(tNumber, index, key, e.target.value)} /></Field>
                        ))}
                      </div>
                    </details>
                  </div>
                ))}
                <button type="button" className="pm-btn-dashed" onClick={() => addParcelToEditPerson(tNumber)}>+ Parcelă în T{tLabel}</button>
              </section>
            );
          })}
          {!Object.keys(editFormData.groupedParcels || {}).length && <p className="pm-note">Această persoană nu mai are parcele. Adaugă un T nou sau anulează.</p>}
          <button type="button" className="pm-btn-soft pm-wide" onClick={addTGroupToEditPerson}>+ Adaugă alt T</button>
        </Panel>
      )}

      {/* ── Asking for a T number ── */}
      {newTPrompt && (
        <Panel title="T nou" onClose={() => setNewTPrompt(null)}
          footer={(
            <>
              <button type="button" className="pm-btn-soft" onClick={() => setNewTPrompt(null)}>Anulează</button>
              <button type="button" className="pm-btn-primary" onClick={confirmNewT}>Adaugă</button>
            </>
          )}>
          <Field label="Număr T" hint={`Are deja: ${newTPrompt.existing || 'niciunul'}`}>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input autoFocus type="text" inputMode="numeric" value={newTPrompt.value}
              onChange={(e) => setNewTPrompt((prev) => ({ ...prev, value: e.target.value.replace(/\D/g, '') }))} />
          </Field>
          <Field label="Comună">
            <select value={newTPrompt.locatie} onChange={(e) => setNewTPrompt((prev) => ({ ...prev, locatie: e.target.value }))}>
              <option value="">Selectează</option>
              {locationNames.map((location) => <option key={location} value={location}>{location}</option>)}
            </select>
          </Field>
        </Panel>
      )}

      {/* ── Choosing a cadastral number ── */}
      {cadastralChoice && (
        <Panel title={cadastralChoice.title} onClose={() => setCadastralChoice(null)}
          footer={<button type="button" className="pm-btn-soft pm-wide" onClick={() => setCadastralChoice(null)}>Nu folosi niciunul</button>}>
          <p className="pm-note pm-preline">{cadastralChoice.message}</p>
          {cadastralChoice.options.map((option) => (
            <button key={option.key} type="button" className="pm-row pm-row-card" onClick={() => chooseCadastral(option)}>
              <span className="pm-row-main">
                <strong>Nr. cadastral {option.cadastralNumber}</strong>
                {option.address && <span>{option.address}</span>}
                {option.surface !== undefined && <span className={option.exact ? 'pm-ok' : 'pm-warn'}>{Number(option.surface).toFixed(2)} ha{option.exact ? ', se potrivește' : ', suprafață diferită'}</span>}
              </span>
            </button>
          ))}
        </Panel>
      )}

      {toast.show && <div className={`pm-toast is-${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
};

export default ParcelManager;
